import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AuthConfigEditor, defaultAuthConfig } from './AuthConfigEditor'
import NavIconPicker from './NavIconPicker'
import ShellLayoutSettings from './ShellLayoutSettings'
import type { AppShellSettings } from '@twasta/page-runtime/shell'
import SiteMenusEditor, { stripPagesFromMenus, type MenuPageOption } from './SiteMenusEditor'
import { convertSiteMenus, getSiteMenuStatus } from '@/api/siteMenus'
import { applyPageTheme, listPageThemes, type ProjectTheme } from '@/api/pageThemes'
import { THEME_PRESETS } from '@twasta/page-runtime/theme'
import type { ProjectAuthConfig, SectionPopup, SiteMenusSpec } from '@twasta/page-schema'
import {
  ChevronRight,
  ChevronDown,
  GripVertical,
  Plus,
  Edit3,
  Trash2,
  Sparkles,
  Download,
  Play,
  FileCode,
  Database,
  GitBranch,
  Save,
  X,
  Loader2,
  FolderArchive,
  FolderOpen,
  LayoutDashboard,
  FileText,
  BarChart3,
  PieChart,
  Cog,
  Upload,
  HardDrive,
  StickyNote,
  Globe,
  Eye,
  MoreVertical,
  Search,
  Home,
  PanelTop,
  Layers,
  Wand2,
  Check,
  ChevronLeft,
  MoreHorizontal,
  LayoutPanelLeft,
  AppWindow,
  Maximize2,
  Pencil,
  History,
  PencilRuler,
} from 'lucide-react'
import { useProjectStore } from '@/store/projectStore'
import { loadProjectProgress, OBJ_TYPE_SINGULAR } from '@/lib/objectTypes'
import AppIcon from '@/components/common/AppIcon'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import {
  getJsonRaw,
  getJson,
  saveJson,
  generateModel,
  generateDatabase,
  deleteDatabase,
  downloadModel,
  checkModelStatus,
  checkBatchModelStatus,
  enhanceSpecification,
  getFrsContent,
  generateAppFromFrs,
} from '@/api/generateapp'
import {
  generatePageObject, generateVisualObject, generateDashboardObject, generateReportObject, getTables,
  loadReportObject, saveReportObject,
} from '@/api/designers'
// Importing an EXISTING Jasper report (.zip bundle / loose .jrxml) instead of
// generating one — the SAME reading and model patch the Report designer's own
// header import uses.
import { blankReportModel, sanitizeReportModel } from '@/pages/designers/report/types'
import { applyImportedFiles, readPickedReportFiles } from '@/pages/designers/report/importBundle'
import api from '@/api/client'

// Schema (obj_type M) generation — file-name == schema-name; visual files
// reference it by SQLMODEL.SCHEMA_NAME so the orchestrator must produce it.
async function generateVisualSchemaForNode(args: {
  project: string
  obj_name: string
  description: string
  functional_specs?: string
}) {
  const res = await api.post('/designers/generate-visual-schema', {
    project: args.project,
    schema_name: args.obj_name,
    description: args.description,
    functional_specs: args.functional_specs || '',
  })
  return res.data
}
import { saveDraft } from '@/api/workbench'
import { DEFAULT_BIZ_LOGIC } from '@/api/projectDb'
import { getSettings } from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { useClaudeAgent } from '@/hooks/useClaudeAgent'
import AgentStreamPanel from '@/components/common/AgentStreamPanel'
import type { FlatNavItem, RawAppData } from '@/types'
import type { PageKind } from '@/pages/designers/page/types'
import { PAGE_KIND_LABELS } from '@/pages/designers/page/types'
import LivePreviewLazyPane from '@/pages/livepreview/LivePreviewPage'
import SmartPageLivePreviewPane from './smartpage-preview/SmartPageLivePreviewPane'
import TransactionDesignerPage from '@/pages/designers/TransactionDesignerPage'
import PageDesignerPage from '@/pages/designers/PageDesignerPage'
import VisualDesignerPage from '@/pages/designers/VisualDesignerPage'
import DashboardDesignerPage from '@/pages/designers/DashboardDesignerPage'
import SchemaDesignerPage from '@/pages/designers/SchemaDesignerPage'
import GenProcessDesignerPage from '@/pages/designers/GenProcessDesignerPage'
import ReportDesignerPage from '@/pages/designers/ReportDesignerPage'
import DocumentDesignerPage from '@/pages/designers/DocumentDesignerPage'
import DocumentFormDesignerPage from '@/pages/designers/DocumentFormDesignerPage'
import WizardDesignerPage from '@/pages/designers/WizardDesignerPage'
import { WizardLivePreviewPane } from '@/pages/designers/wizard/PreviewTab'
import { DocumentRepositoryPane } from '@/pages/designers/document/DocumentRunPane'
import { SourceEditor } from '@/pages/srceditor/SourceEditorPage'
import { VisualLivePreviewPane } from '@/pages/designers/visual/VisualPreviewModal'
import { DashboardLivePreviewPane } from '@/pages/designers/dashboard/DashboardPreviewModal'
import { GenProcessLivePreviewPane } from '@/pages/designers/genprocess/RunPanel'
import { ReportLivePreviewPane } from '@/pages/designers/report/ReportLivePreviewPane'
import { loadVisualCatalog } from '@/pages/designers/visual/catalog'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import Modal from '@/components/common/Modal'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import AppTree, { arrangeSiteChildren, getChildren, typeName, TYPE_ICON } from '@/components/workbench/AppTree'
import SiteEditFrame from './SiteEditFrame'
import { useJourneyStore, refreshJourney } from '@/store/journeyStore'
import { type JourneyObject, type JourneyTab, type ObjectStatus } from '@/api/journey'
import { CheckScreensPanel, ScreenTestLink, ScreenTestModal, screenStatus } from './simple/ScreenChecks'
import { useJourneyAction, runUrl } from '@/lib/journeyActions'
import { prefillDock } from '@/store/dockBridgeStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { useObjectViewStore, type ObjectViewKind, type ObjectViewMode } from '@/store/objectViewStore'
import { getScmLog } from '@/api/workbench'
import { createFlowForm, type AvailableFlow } from '@/api/flowForms'
import FlowNodePicker, { flowIdFromSpecs, slugForObjName } from './FlowNodePicker'
import FlowRunPane from '@/pages/designers/scheduler/FlowRunPane'
import vocabulary from './simple/vocabulary.json'
import './simple/simple.css'

/* ── Constants ────────────────────────────────────────── */

// Object types whose Layout tab embeds a full designer (transaction / page /
// visual / dashboard / visual-schema / general-process). Other types
// (folders, external websites) have no embeddable editor. Business Areas (A)
// are NOT navigation objects — they're managed under Advanced → Configure AI.
const NODE_HAS_DESIGNER = ['T', 'S', 'V', 'D', 'M', 'G', 'R', 'K', 'E', 'U']

// Object types that get a workbench "Live Preview" tab (a runtime/chart
// preview separate from the Design editor). A wizard (U) runs its SAVED model
// there, as the Run view will; the designer's own Preview tab runs it too.
const NODE_HAS_LIVE_PREVIEW = ['T', 'V', 'D', 'G', 'R', 'K', 'U', 'J']

/** Whether a node's live preview can open. An Integration Flow (J) has no
 *  model of its own to wait for — its pane reads the flow from IOFlow and
 *  offers to make the input form when there is none (docs/flow_forms.md). */
function hasLivePreview(objType: string, modelExists: boolean): boolean {
  return NODE_HAS_LIVE_PREVIEW.includes(objType) && (modelExists || objType === 'J')
}

// Type names come from the shared vocabulary (lib/objectTypes) — the same one
// the Overview breakdown labels its rows with.
const OBJ_TYPE_MAP = OBJ_TYPE_SINGULAR

const OBJ_TYPE_OPTIONS = [
  { value: '-', label: 'Folder' },
  { value: 'T', label: 'Transaction' },
  { value: 'V', label: 'Visual' },
  { value: 'M', label: 'Visual Schema' },
  { value: 'D', label: 'Dashboard' },
  { value: 'R', label: 'Report' },
  { value: 'G', label: 'General Process' },
  { value: 'U', label: 'Wizard' },
  { value: 'J', label: 'Integration Flow' },
  { value: 'K', label: 'Document Repository' },
  { value: 'S', label: 'Smart Page' },
  { value: 'X', label: 'External Website' },
]

const THEME_COLORS = ['#3F51B5', '#4CAF50', '#FF5722', '#009688', '#E91E63']

/* ── Helpers ──────────────────────────────────────────── */

/**
 * A `material-icons` span renders its text content as a LIGATURE, so anything
 * that isn't an icon name comes out mangled: an app imported by reverse deploy
 * carries the legacy `ITM2MENU.icon_path` through as `icon_image` (e.g.
 * "reports.png"), the font substitutes the longest prefix it recognises
 * ("report") into one wide glyph and leaves the rest ("s.png") as plain text
 * beside it — which is why such a heading looks differently broken per item.
 * Icon names are always [a-z0-9_]; render anything else as the fallback while
 * leaving the stored value alone, so it still deploys back as an icon_path.
 */
function materialIcon(raw: string | undefined | null, fallback = 'description'): string {
  const name = (raw || '').trim()
  return /^[a-z0-9_]+$/.test(name) ? name : fallback
}

/**
 * Some LLMs emit per-parent ordinals as `id` (e.g. "1", "2") and put the
 * dotted hierarchical path into `menu_path` only. With duplicate ids the
 * tree builder matches multiple items per parent_id and produces phantom
 * nestings. This helper rebuilds id/parent_id so they are globally unique
 * dotted paths without changing the underlying hierarchy.
 */
function normalizeAppData<T extends RawAppData | null | undefined>(data: T): T {
  if (!data || !Array.isArray(data.navigation) || data.navigation.length === 0) return data
  const nav = data.navigation
  const appId = data.application?.id || ''

  const ids = nav.map((n) => String(n.id ?? '').trim())
  const idsUnique = new Set(ids).size === ids.length && ids.every(Boolean)
  const idsHierarchical = ids.some((i) => i.includes('.'))
  if (idsUnique && idsHierarchical) return data

  const menuPaths = nav.map((n) => String(n.menu_path ?? '').trim())
  const mpUnique = new Set(menuPaths).size === menuPaths.length && menuPaths.every(Boolean)
  const mpHierarchical = menuPaths.some((p) => p.includes('.'))

  if (mpUnique && mpHierarchical) {
    const fixed = nav.map((n) => {
      const mp = String(n.menu_path ?? '').trim()
      if (!mp) return n
      let parent_id = ''
      if (mp.includes('.')) {
        const parentPath = mp.slice(0, mp.lastIndexOf('.'))
        parent_id = parentPath === appId ? '' : parentPath
      }
      return { ...n, id: mp, parent_id }
    })
    return { ...data, navigation: fixed }
  }

  // Fallback: rebuild from per-parent ordinals + parent_id graph (in order).
  const oldIds = ids
  const oldParentIds = nav.map((n) => String(n.parent_id ?? '').trim())
  const newIds: string[] = new Array(nav.length).fill('')
  const siblingCount = new Map<string, number>()
  const fixed = nav.map((n, i) => {
    const oldPid = oldParentIds[i]
    let newPid = ''
    if (oldPid) {
      for (let j = i - 1; j >= 0; j--) {
        if (oldIds[j] === oldPid) {
          newPid = newIds[j]
          break
        }
      }
    }
    const ordinal = (siblingCount.get(newPid) ?? 0) + 1
    siblingCount.set(newPid, ordinal)
    const newId = newPid ? `${newPid}.${ordinal}` : appId ? `${appId}.${ordinal}` : String(ordinal)
    newIds[i] = newId
    return { ...n, id: newId, parent_id: newPid, menu_path: newId }
  })
  return { ...data, navigation: fixed }
}

function generateId(nav: FlatNavItem[], parentId: string): string {
  const prefix = parentId || 'node'
  const siblings = nav.filter((n) => n.parent_id === (parentId || ''))
  const nextNum = siblings.length + 1
  return `${prefix}.${nextNum}`
}

/* ── PageKindTilePicker ──────────────────────────────────
 *
 * Three-tile chooser for the `kind` of a Smart Page. Lives in the
 * EditNodeModal (only when obj_type === 'S'). Editable both at creation
 * and after — authors can flip an existing page between document /
 * internal / external as their intent for the page evolves.
 *
 * Stays in this file (rather than its own module) because it has no
 * other consumer; if a second one ever appears, lift it to
 * `frontend/src/components/common/`.
 */
const PAGE_KIND_TILE_DESCRIPTIONS: Record<PageKind, string> = {
  internal: 'Login-gated page in your Vision app.',
  external: 'Public page on your tenant subdomain or custom domain.',
  document: 'Notion-style template that spawns document instances.',
}

function PageKindTilePicker({
  value,
  disabled,
  onChange,
}: {
  value: PageKind
  disabled: boolean
  onChange: (k: PageKind) => void
}) {
  const tiles: PageKind[] = ['internal', 'external', 'document']
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
        Page kind
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
        {tiles.map((k) => {
          const selected = value === k
          return (
            <button
              key={k}
              type="button"
              disabled={disabled}
              onClick={() => onChange(k)}
              style={{
                textAlign: 'left',
                padding: '0.625rem 0.75rem',
                border: '1px solid ' + (selected ? 'var(--color-accent)' : 'var(--color-border)'),
                borderRadius: 'var(--radius-md)',
                background: selected ? 'var(--color-accent)' : 'var(--color-bg)',
                color: selected ? 'var(--pg-on-primary, #fff)' : 'var(--color-text)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled && !selected ? 0.5 : 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                minHeight: 78,
              }}
            >
              <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                {PAGE_KIND_LABELS[k]}
              </span>
              <span style={{
                fontSize: '0.8125rem',
                lineHeight: 1.35,
                opacity: 0.85,
              }}>
                {PAGE_KIND_TILE_DESCRIPTIONS[k]}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/* ── ExternalWebsiteSpecForm ──────────────────────────────
 *
 * Structured editor for the JSON that lives in an X node's
 * `functional_specs`. The shape mirrors what
 * `backend/app/services/site_from_nav.py` reads to build a deployable
 * site manifest (slug, title, is_primary, theme, custom_domains).
 * Form values serialize back into a single JSON
 * string so the surrounding save flow is unchanged — the form is a
 * drop-in replacement for the free-text textarea.
 *
 * Stays in this file (no second consumer); lift to common/ if reused.
 */
interface WebsiteSpec {
  slug?: string
  title?: string
  is_primary?: boolean
  /** `{ref}` = a PROJECT theme (Page_Design/themes/<id>.json) the site's
   *  pages wear; `{preset}` = a built-in preset. */
  theme?: { preset?: string; ref?: string }
  custom_domains?: { hostname: string }[]
  // obj_name of the Smart Page designed as the site header/footer. Empty
  // ⇒ fall back to the auto navigation menu (header) / default footer.
  header_page_obj_name?: string
  footer_page_obj_name?: string
  // obj_name of a Smart Page shown site-wide inside a pop-up modal (like the
  // header/footer chrome, but triggered on demand). Empty ⇒ no site popup.
  popup_page_obj_name?: string
  // Trigger + modal presentation for the site popup. Reuses the same shape as
  // a section-level popup so the runtime renders it through PopupLauncher.
  popup_config?: SitePopupConfig
  // Auto-hide the site header on scroll-down, reveal on scroll-up. Defaults
  // to ON when unset; set false to keep the header always visible (sticky).
  // Legacy — superseded by header_scroll (kept in sync for older runtimes).
  header_auto_hide?: boolean
  // How the site header behaves on scroll:
  //   'static'    — scrolls out of view with the page.
  //   'sticky'    — pinned to the top, always visible.
  //   'auto-hide' — pinned; hides on scroll-down, reveals on scroll-up.
  header_scroll?: HeaderScrollBehavior
  // The site's NAMED menus (header, footer, …) — what a nav_menu block with
  // `menu: <name>` shows. See SiteMenusEditor.
  menus?: SiteMenusSpec
  // Per-page slug / nav label / home flag / collection template binding.
  page_overrides?: Record<string, { collection?: { role?: string } } & Record<string, unknown>>
}

type HeaderScrollBehavior = 'static' | 'sticky' | 'auto-hide'

// Mirror of SectionPopup (packages/page-schema) for the site-wide popup.
type SitePopupConfig = SectionPopup

/** Every external (kind) Smart Page anywhere under `rootId`, in document
 *  order. Used to populate the header/footer pickers — the chosen page
 *  renders as site-wide chrome (and is excluded from the routable pages). */
function descendantSmartPages(nav: FlatNavItem[], rootId: string): FlatNavItem[] {
  const out: FlatNavItem[] = []
  const walk = (parentId: string) => {
    for (const child of getChildren(nav, parentId)) {
      if (child.obj_type === 'S') {
        if (!child.page_kind || child.page_kind === 'external') out.push(child)
      } else {
        walk(child.id)
      }
    }
  }
  walk(rootId)
  return out
}

// Saved site specs from before the preset rewrite name these.
const LEGACY_SITE_PRESET: Record<string, string> = {
  modern: 'modern-light', minimal: 'minimal-pro', dark: 'modern-dark', classic: 'modern-light',
}

function parseWebsiteSpec(raw: string | undefined): WebsiteSpec {
  // Free-text legacy specs (or empty) → empty object so the form
  // starts blank rather than crashing. The user can re-author from the
  // form even if the previous value was prose.
  if (!raw || !raw.trim()) return {}
  try {
    const v = JSON.parse(raw)
    return typeof v === 'object' && v !== null ? v as WebsiteSpec : {}
  } catch {
    return {}
  }
}

function ExternalWebsiteSpecForm({
  rawSpecs,
  defaultSlug,
  sitePages = [],
  onChange,
  legacyMenus,
  project = '',
  siteObj = '',
}: {
  rawSpecs: string
  defaultSlug: string
  /** Project + the saved site's obj_name — enable the project-theme picker
   *  and "Apply to every page". */
  project?: string
  siteObj?: string
  /** External Smart Pages under this site, for the header/footer pickers. */
  sitePages?: { obj_name: string; title: string }[]
  onChange: (jsonString: string) => void
  /** The site still decides menus by folder position — offer to convert. */
  legacyMenus?: { active: boolean; busy: boolean; disabledReason?: string; onConvert: () => void }
}) {
  const spec = useMemo(() => parseWebsiteSpec(rawSpecs), [rawSpecs])
  // Pages a menu can link to: the site's pages minus its header/footer/pop-up
  // (chrome, no URL of their own); collection TEMPLATES are flagged so the
  // menu editor never offers them.
  const menuPages: MenuPageOption[] = useMemo(() => {
    const chrome = new Set([spec.header_page_obj_name, spec.footer_page_obj_name, spec.popup_page_obj_name].filter(Boolean))
    const overrides = spec.page_overrides || {}
    return sitePages
      .filter((p) => !chrome.has(p.obj_name))
      .map((p) => {
        const role = overrides[p.obj_name]?.collection?.role
        return { obj_name: p.obj_name, title: p.title, template: role === 'item' || role === 'taxonomy' }
      })
  }, [sitePages, spec.header_page_obj_name, spec.footer_page_obj_name, spec.popup_page_obj_name, spec.page_overrides])

  const update = (patch: Partial<WebsiteSpec>) => {
    const next = { ...spec, ...patch }
    onChange(JSON.stringify(next, null, 2))
  }

  const slug = spec.slug ?? defaultSlug
  const title = spec.title ?? ''
  const isPrimary = spec.is_primary ?? true
  const themePreset = LEGACY_SITE_PRESET[spec.theme?.preset ?? ''] ?? spec.theme?.preset ?? 'modern-light'
  const themeValue = spec.theme?.ref ? `ref:${spec.theme.ref}` : `preset:${themePreset}`
  const [projectThemes, setProjectThemes] = useState<ProjectTheme[]>([])
  const [applyingTheme, setApplyingTheme] = useState(false)
  useEffect(() => {
    if (!project) return
    let live = true
    listPageThemes(project)
      .then((o) => { if (live) setProjectThemes(o.themes) })
      .catch(() => { /* the picker still offers the built-in presets */ })
    return () => { live = false }
  }, [project])
  const applySiteTheme = async () => {
    const id = spec.theme?.ref || themePreset
    setApplyingTheme(true)
    try {
      const res = await applyPageTheme(project, { theme: id, site: siteObj })
      toast.success(`${res.pages.length} page${res.pages.length === 1 ? '' : 's'} of this website now use the theme.`)
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Could not apply the theme.')
    } finally {
      setApplyingTheme(false)
    }
  }
  const domains = spec.custom_domains ?? []
  const headerPage = spec.header_page_obj_name ?? ''
  const footerPage = spec.footer_page_obj_name ?? ''
  const popupPage = spec.popup_page_obj_name ?? ''
  const popupCfg: SitePopupConfig = spec.popup_config ?? { trigger: { style: 'fab', label: 'Open', position: 'bottom-right' } }
  // Header scroll behaviour: prefer the explicit header_scroll; fall back to the
  // legacy header_auto_hide boolean (false ⇒ sticky, else auto-hide).
  const headerScroll: HeaderScrollBehavior =
    spec.header_scroll ?? (spec.header_auto_hide === false ? 'sticky' : 'auto-hide')
  const updatePopupCfg = (patch: Partial<SitePopupConfig>) =>
    update({ popup_config: { ...popupCfg, ...patch } })
  const updatePopupTrigger = (patch: Partial<SitePopupConfig['trigger']>) =>
    update({ popup_config: { ...popupCfg, trigger: { ...popupCfg.trigger, ...patch } } })

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '0.5rem 0.75rem',
    border: '1px solid var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--color-input-bg)', color: 'var(--color-text)',
    fontSize: '0.875rem',
  }
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.8125rem', fontWeight: 600,
    marginBottom: 4, color: 'var(--color-text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }

  return (
    <div style={{ display: 'grid', gap: '0.85rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
        <div>
          <label style={labelStyle}>Site slug</label>
          <input
            value={slug}
            onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
            placeholder="main"
            style={fieldStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Title</label>
          <input
            value={title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Acme Corp"
            style={fieldStyle}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
        <div>
          <label style={labelStyle}>Site theme</label>
          <select
            value={themeValue}
            onChange={(e) => {
              const [kind, id] = e.target.value.split(/:(.*)/s)
              update({ theme: kind === 'ref' ? { ref: id } : { preset: id } })
            }}
            style={fieldStyle}
          >
            {projectThemes.length > 0 && (
              <optgroup label="Project themes">
                {projectThemes.map((t) => <option key={t.id} value={`ref:${t.id}`}>{t.name}</option>)}
              </optgroup>
            )}
            {spec.theme?.ref && !projectThemes.some((t) => t.id === spec.theme?.ref) && (
              <option value={`ref:${spec.theme.ref}`}>{spec.theme.ref} (missing)</option>
            )}
            <optgroup label="Built-in presets">
              {THEME_PRESETS.map((p) => <option key={p.id} value={`preset:${p.id}`}>{p.label}</option>)}
            </optgroup>
          </select>
          {project && siteObj && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={applyingTheme}
                      onClick={() => void applySiteTheme()}
                      title="Bind every page of this website (header and footer included) to this theme">
                {applyingTheme ? 'Applying…' : 'Apply to every page'}
              </button>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                New pages follow it; pages with colours of their own change when you apply.
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.875rem' }}>
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => update({ is_primary: e.target.checked })}
            />
            Primary site (owns the tenant's default subdomain root)
          </label>
        </div>
      </div>

      {/* Header / footer chrome pickers — choose a designed Smart Page to
          render as the site-wide header/footer (with its logo + links). The
          chosen page is removed from the routable pages + auto menu. Leave on
          "default" to use the auto navigation bar / a simple default footer. */}
      <div>
        <label style={labelStyle}>Header &amp; footer</label>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
          Pick a Smart Page to use as the site header or footer (design it with
          a logo and links like any page). The chosen page won't appear as a
          separate site page. Leave on “default” to use the automatic nav bar
          and footer.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
          <div>
            <label style={labelStyle}>Header page</label>
            <select
              value={headerPage}
              onChange={(e) => update({ header_page_obj_name: e.target.value || undefined })}
              style={fieldStyle}
            >
              <option value="">— Default navigation bar —</option>
              {sitePages.map((p) => (
                <option key={p.obj_name} value={p.obj_name}>{p.title || p.obj_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Footer page</label>
            <select
              value={footerPage}
              onChange={(e) => update({ footer_page_obj_name: e.target.value || undefined })}
              style={fieldStyle}
            >
              <option value="">— Default footer —</option>
              {sitePages.map((p) => (
                <option key={p.obj_name} value={p.obj_name}>{p.title || p.obj_name}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Header scroll behaviour — defaults to auto-hide. We also keep the
            legacy header_auto_hide in sync so older deploys/runtimes that only
            read that flag still behave sensibly (only 'auto-hide' ⇒ true). */}
        <div style={{ marginTop: 8 }}>
          <label style={labelStyle}>Header scroll behaviour</label>
          <select
            value={headerScroll}
            onChange={(e) => {
              const v = e.target.value as HeaderScrollBehavior
              update({ header_scroll: v, header_auto_hide: v === 'auto-hide' })
            }}
            style={fieldStyle}
          >
            <option value="static">Scroll with page (header scrolls away)</option>
            <option value="sticky">Sticky (always visible at top)</option>
            <option value="auto-hide">Auto-hide (hide on scroll down, reveal on scroll up)</option>
          </select>
        </div>
      </div>

      {/* Named menus — which links the header / footer (or any nav_menu
          block) show. Site data, independent of the navigation tree. */}
      <div>
        <label style={labelStyle}>Menus</label>
        <SiteMenusEditor
          menus={spec.menus}
          pages={menuPages}
          legacy={legacyMenus}
          onChange={(menus) => {
            const next = { ...spec }
            if (menus) next.menus = menus
            else delete next.menus
            onChange(JSON.stringify(next, null, 2))
          }}
        />
      </div>

      {/* Site-wide pop-up — pick a Smart Page shown in a modal triggered on
          every page (like header/footer chrome, but on demand). */}
      <div>
        <label style={labelStyle}>Site pop-up</label>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.45 }}>
          Pick a Smart Page to show site-wide inside a pop-up modal — a floating
          trigger appears on every page and opens the page's content in a dialog.
          Leave on “none” for no site pop-up.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
          <div>
            <label style={labelStyle}>Pop-up page</label>
            <select
              value={popupPage}
              onChange={(e) => update({ popup_page_obj_name: e.target.value || undefined })}
              style={fieldStyle}
            >
              <option value="">— None —</option>
              {sitePages.map((p) => (
                <option key={p.obj_name} value={p.obj_name}>{p.title || p.obj_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Trigger style</label>
            <select
              value={popupCfg.trigger.style}
              onChange={(e) => updatePopupTrigger({ style: e.target.value as SitePopupConfig['trigger']['style'] })}
              style={fieldStyle}
              disabled={!popupPage}
            >
              <option value="fab">Floating button</option>
              <option value="icon">Floating icon</option>
              <option value="link">Inline link</option>
            </select>
          </div>
        </div>
        {popupPage && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.85rem', marginTop: '0.6rem' }}>
            <div>
              <label style={labelStyle}>Trigger label</label>
              <input
                value={popupCfg.trigger.label ?? ''}
                onChange={(e) => updatePopupTrigger({ label: e.target.value })}
                placeholder="Open"
                style={fieldStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Modal size</label>
              <select
                value={popupCfg.modalSize ?? 'md'}
                onChange={(e) => updatePopupCfg({ modalSize: e.target.value as SitePopupConfig['modalSize'] })}
                style={fieldStyle}
              >
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Position</label>
              <select
                value={popupCfg.modalPosition ?? 'center'}
                onChange={(e) => updatePopupCfg({ modalPosition: e.target.value as SitePopupConfig['modalPosition'] })}
                style={fieldStyle}
              >
                <option value="center">Center</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left drawer</option>
                <option value="right">Right drawer</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Custom domains list */}
      <div>
        <label style={labelStyle}>Custom domains</label>
        <div style={{ display: 'grid', gap: 6 }}>
          {domains.map((d, i) => (
            <div key={i} style={{ display: 'flex', gap: 6 }}>
              <input
                value={d.hostname}
                onChange={(e) => {
                  const next = domains.slice()
                  next[i] = { hostname: e.target.value.toLowerCase().trim() }
                  update({ custom_domains: next })
                }}
                placeholder="www.example.com"
                style={{ ...fieldStyle, flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => update({ custom_domains: domains.filter((_, j) => j !== i) })}
                title="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => update({ custom_domains: [...domains, { hostname: '' }] })}
            style={{ alignSelf: 'flex-start', fontSize: '0.8125rem' }}
          >
            <Plus size={12} /> Add domain
          </button>
        </div>
      </div>

    </div>
  )
}

/* ── EditNodeModal ───────────────────────────────────── */

interface EditNodeModalProps {
  node: FlatNavItem | null
  isNew: boolean
  project: string
  generationMode: string
  effortConfig: Record<string, string>
  /** Full navigation list — used to populate the website header/footer pickers. */
  navItems: FlatNavItem[]
  onSave: (node: FlatNavItem) => void
  onClose: () => void
  /** When true the form renders flush inside the Details tab (no slide-in
      overlay, no header/Cancel) instead of as a right-hand modal. */
  inline?: boolean
  /** Convert a website's legacy menu folders into named menus (server-side:
      moves nodes and rewrites nav_menu blocks), then reload. */
  onConvertMenus?: (siteId: string) => Promise<void>
}

function EditNodeModal({ node, isNew, project, generationMode, effortConfig, navItems, onSave, onClose, inline = false, onConvertMenus }: EditNodeModalProps) {
  const [form, setForm] = useState<FlatNavItem>(
    node || {
      id: '',
      title: '',
      parent_id: '',
      icon_image: 'description',
      description: '',
      obj_name: '',
      obj_type: 'T',
      menu_path: '',
      functional_specs: '',
      design_reference_url: '',
      clone_reference: false,
      order: 0,
    }
  )
  const [enhancing, setEnhancing] = useState(false)
  const [agentPanelOpen, setAgentPanelOpen] = useState(false)

  // Claude Code Agent for spec enhancement
  const agent = useClaudeAgent({ project })
  const prevStreamingRef = useRef(false)

  // Auto-apply enhanced spec when agent finishes (text-only task)
  useEffect(() => {
    if (prevStreamingRef.current && !agent.isStreaming) {
      const lastAssistant = [...agent.messages].reverse().find((m) => m.role === 'assistant')
      if (lastAssistant?.content?.trim()) {
        setForm((prev) => ({ ...prev, functional_specs: lastAssistant.content }))
        toast.success('Specification enhanced by AI')
      }
      setEnhancing(false)
    }
    prevStreamingRef.current = agent.isStreaming
  }, [agent.isStreaming, agent.messages])

  const isFolder = form.obj_type === '-'

  // Does this website still decide its menus by folder position? Asked of
  // the server (it reads the site's nav_menu blocks) for a saved site only.
  const [legacyMenus, setLegacyMenus] = useState(false)
  const [convertingMenus, setConvertingMenus] = useState(false)
  const siteNodeId = !isNew && node?.obj_type === 'X' ? node.id : ''
  useEffect(() => {
    if (!siteNodeId || !project || !onConvertMenus) { setLegacyMenus(false); return }
    let cancelled = false
    getSiteMenuStatus(project, siteNodeId)
      .then((r) => { if (!cancelled) setLegacyMenus(!!r.legacy) })
      .catch((err) => {
        // Not fatal — the form still edits menus; only the Convert offer is lost.
        console.warn('[site-menus] status check failed', err)
        if (!cancelled) setLegacyMenus(false)
      })
    return () => { cancelled = true }
  }, [siteNodeId, project, onConvertMenus])

  // Unsaved-change detection — drives the inline Details "Save" highlight so
  // the button stands out the moment the spec is edited.
  const isDirty = isNew || !node || JSON.stringify(form) !== JSON.stringify(node)

  const handleChange = (field: keyof FlatNavItem, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    if (!form.title.trim()) {
      toast.error('Title is required')
      return
    }
    if (!isFolder && !form.obj_name.trim()) {
      toast.error('Object name is required')
      return
    }
    if (form.obj_type === 'J') {
      void saveFlowNode()
      return
    }
    onSave(form)
  }

  // An Integration Flow item (obj_type "J", docs/flow_forms.md): a MANUAL
  // flow's input form is generated as the transaction named after the item
  // before the item is saved — so the menu never shows an item whose form
  // does not exist. Re-saving an item whose form already belongs to this
  // flow leaves the designed form untouched (the server decides).
  const [savingFlow, setSavingFlow] = useState(false)
  const [pickedFlow, setPickedFlow] = useState<AvailableFlow | null>(null)
  const saveFlowNode = async () => {
    const flowId = flowIdFromSpecs(form.functional_specs)
    if (!flowId) {
      toast.error('Choose the IOFlow flow this item runs')
      return
    }
    if (pickedFlow && pickedFlow.trigger_type !== 'manual') {
      onSave(form)
      return
    }
    setSavingFlow(true)
    try {
      const res = await createFlowForm(project, form.obj_name.trim(), flowId)
      if (res.created) toast.success('Input form created — open it in the designer to add look-ups and rules')
      onSave(form)
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      const detail = err?.response?.data?.detail || ''
      // A flow that is not started by hand takes no form; the item still saves.
      if (detail.startsWith('Only a flow started by hand')) {
        onSave(form)
      } else {
        toast.error(detail || 'The input form could not be created')
      }
    } finally {
      setSavingFlow(false)
    }
  }

  const handleEnhance = async () => {
    if (!form.functional_specs.trim() && !form.description.trim()) {
      toast.error('Add a description or specification first')
      return
    }

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      setAgentPanelOpen(true)
      setEnhancing(true)
      agent.clearMessages()
      agent.startTask('enhance_spec', {
        specification: form.functional_specs,
        description: form.description,
        obj_type: form.obj_type,
        effort: effortConfig.enhance_spec,
      })
      return
    }

    // LLM API mode (existing behavior)
    setEnhancing(true)
    try {
      const res = await enhanceSpecification({
        specification: form.functional_specs,
        description: form.description,
        project,
        obj_name: form.obj_name,
        obj_type: form.obj_type,
      })
      const enhanced = res.enhanced_specification || res.specification || ''
      if (enhanced) {
        setForm((prev) => ({ ...prev, functional_specs: enhanced }))
        toast.success('Specification enhanced by AI')
      }
    } catch {
      toast.error('Failed to enhance specification')
    } finally {
      setEnhancing(false)
    }
  }

  return (
    <div
      style={inline ? {
        position: 'relative',
        width: '100%',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
      } : {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: agentPanelOpen ? '80%' : '60%',
        maxWidth: agentPanelOpen ? 1100 : 700,
        background: 'var(--color-bg)',
        borderLeft: '1px solid var(--color-border)',
        boxShadow: '-10px 0 30px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'row',
        zIndex: 100,
        animation: 'slideInRight 250ms ease-out',
        transition: 'width 250ms ease, max-width 250ms ease',
      }}
    >
      {/* Form panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header — only the modal variant needs a title bar + close button;
            inline the Details tab already shows the title/badge above. */}
        {!inline && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              borderBottom: '1px solid var(--color-border)',
              flexShrink: 0,
            }}
          >
            <h3 style={{ fontSize: '1.0625rem', fontWeight: 600 }}>
              {isNew ? 'Add Navigation Item' : 'Edit Navigation Item'}
            </h3>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        )}

        {/* Form */}
        <div style={{ flex: inline ? '0 0 auto' : 1, overflow: inline ? 'visible' : 'auto', padding: inline ? 0 : '1.25rem' }}>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {/* Icon */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                Icon
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  value={form.icon_image}
                  onChange={(e) => handleChange('icon_image', e.target.value)}
                  placeholder="material icon name"
                  style={{ flex: 1 }}
                />
                <span
                  className="material-icons"
                  style={{
                    fontSize: 25,
                    color: 'var(--color-accent)',
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  {materialIcon(form.icon_image)}
                </span>
              </div>
            </div>

            {/* Menu icon — what the running application's shell draws. */}
            <NavIconPicker value={form.icon} objType={form.obj_type}
              onChange={(icon) => handleChange('icon', icon)} />

            {/* Title */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                Title *
              </label>
              <input
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Navigation item title"
              />
            </div>

            {/* Object Type */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                Type
              </label>
              <select
                value={form.obj_type}
                onChange={(e) => {
                  const next = e.target.value
                  setForm((prev) => ({
                    ...prev,
                    obj_type: next,
                    // Default page_kind when switching to Smart Page; clear
                    // it for any other type so it doesn't leak across kinds.
                    page_kind: next === 'S' ? (prev.page_kind || 'internal') : undefined,
                  }))
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)',
                  fontSize: '0.9375rem',
                }}
              >
                {/* A node can carry a type this list doesn't know — an import
                    from a system whose vocabulary has a code we don't map. A
                    <select> with no matching option renders its FIRST one, so
                    such a node read as "Folder" (the first entry) and editing
                    it silently rewrote its type. Offer the raw code instead,
                    so the mismatch is visible and survives a save. */}
                {!OBJ_TYPE_OPTIONS.some((opt) => opt.value === form.obj_type) && (
                  <option value={form.obj_type}>
                    {OBJ_TYPE_MAP[form.obj_type] || `Unknown (${form.obj_type || 'none'})`}
                  </option>
                )}
                {OBJ_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Kind picker — only shown for Smart Pages. Editable
                both at creation and after, so authors can flip an
                existing page between document / internal / external. */}
            {form.obj_type === 'S' && (
              <PageKindTilePicker
                value={(form.page_kind as PageKind | undefined) ?? 'internal'}
                disabled={false}
                onChange={(k) => setForm((prev) => ({ ...prev, page_kind: k }))}
              />
            )}

            {/* Object Name (hidden for folders) */}
            {!isFolder && (
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                  Object Name *
                </label>
                <input
                  value={form.obj_name}
                  onChange={(e) => handleChange('obj_name', e.target.value)}
                  placeholder="e.g. sales_order"
                />
              </div>
            )}

            {form.obj_type === 'J' && (
              <FlowNodePicker
                project={project}
                rawSpecs={form.functional_specs}
                originalFlowId={!isNew && node?.obj_type === 'J' ? flowIdFromSpecs(node.functional_specs) : ''}
                onPick={(f) => {
                  setPickedFlow(f)
                  setForm((prev) => ({
                    ...prev,
                    functional_specs: JSON.stringify({ flow_id: f.flow_id }),
                    title: prev.title.trim() ? prev.title : f.name,
                    obj_name: prev.obj_name.trim() ? prev.obj_name : slugForObjName(f.name),
                  }))
                }}
              />
            )}

            {/* Description */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                placeholder="Brief description of this item"
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-bg)',
                  color: 'var(--color-text)',
                  fontSize: '0.9375rem',
                  resize: 'vertical',
                  fontFamily: 'var(--font-primary)',
                }}
              />
            </div>

            {/* External Website specification — structured form
                stored as JSON in functional_specs. Replaces the free-
                text textarea so the deployer can read structured fields
                (slug, theme, domains, API requirements). */}
            {form.obj_type === 'X' && (
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 6 }}>
                  Website Specification
                </label>
                <ExternalWebsiteSpecForm
                  rawSpecs={form.functional_specs}
                  defaultSlug={form.obj_name || ''}
                  project={project}
                  siteObj={siteNodeId ? (node?.obj_name || '') : ''}
                  sitePages={descendantSmartPages(navItems, form.id).map((p) => ({
                    obj_name: p.obj_name, title: p.title,
                  }))}
                  onChange={(json) => handleChange('functional_specs', json)}
                  legacyMenus={siteNodeId && onConvertMenus ? {
                    active: legacyMenus,
                    busy: convertingMenus,
                    disabledReason: isDirty ? 'Save your changes first.' : undefined,
                    onConvert: () => {
                      setConvertingMenus(true)
                      onConvertMenus(siteNodeId)
                        .then(() => setLegacyMenus(false))
                        .catch(() => { /* the caller reports the failure */ })
                        .finally(() => setConvertingMenus(false))
                    },
                  } : undefined}
                />
              </div>
            )}

            {/* Functional Specification (hidden for folders + websites,
                which use the structured form above). */}
            {!isFolder && form.obj_type !== 'X' && form.obj_type !== 'J' && (
              <div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 4,
                  }}
                >
                  <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    Functional Specification
                  </label>
                  {(form.obj_type === 'T' || form.obj_type === 'D') && (
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={handleEnhance}
                      disabled={enhancing}
                      style={{ fontSize: '0.8125rem', gap: 4 }}
                    >
                      {enhancing ? (
                        <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Sparkles size={12} />
                      )}
                      {enhancing ? 'Enhancing...' : 'Enhance with AI'}
                    </button>
                  )}
                </div>
                <textarea
                  value={form.functional_specs}
                  onChange={(e) => handleChange('functional_specs', e.target.value)}
                  rows={10}
                  placeholder="Detailed functional specification..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                    fontSize: '0.875rem',
                    resize: 'vertical',
                    fontFamily: 'monospace',
                    lineHeight: 1.6,
                  }}
                />
              </div>
            )}

            {/* Reference URL — Smart Page only. The backend fetches and
                cleans the page text, then includes it in the generation
                prompt. The user's description above directs how it's used
                (design cues, content source, or both). */}
            {form.obj_type === 'S' && (
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                  Reference URL
                </label>
                <input
                  type="url"
                  value={form.design_reference_url || ''}
                  onChange={(e) => handleChange('design_reference_url', e.target.value)}
                  placeholder="https://example.com/page-to-reference"
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  Optional. The page at this URL is fetched and its text is included in
                  the AI prompt. In your description above, tell the AI how to use it —
                  e.g. <em>“use it as a design reference”</em>, <em>“use its content as the
                  source for the FAQ section”</em>, or both.
                </div>

                {/* High-fidelity clone toggle — when checked, generation
                    routes through the Claude Code agent so it can fetch
                    the URL, render JS-heavy pages with Playwright via
                    Bash, take a screenshot, and use vision to reproduce
                    the layout as closely as the block schema allows.
                    Disabled until a URL is entered. */}
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    marginTop: 10,
                    cursor: form.design_reference_url ? 'pointer' : 'not-allowed',
                    opacity: form.design_reference_url ? 1 : 0.55,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!form.clone_reference}
                    disabled={!form.design_reference_url}
                    onChange={(e) => handleChange('clone_reference', e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: '0.875rem' }}>
                    <strong>Clone this page as closely as possible</strong>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                      Uses the Claude Code agent — fetches and renders the page (handles
                      JS-heavy sites), takes a screenshot, and uses vision to recreate the
                      layout. Slower and higher cost, but much higher fidelity. Fidelity is
                      still bounded by the available block types.
                    </div>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            padding: inline ? '1rem 0 0' : '1rem 1.25rem',
            borderTop: '1px solid var(--color-border)',
            flexShrink: 0,
            marginTop: inline ? '1rem' : 0,
          }}
        >
          {!inline && (
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          )}
          <button
            className={`btn ${inline && !isDirty ? 'btn-secondary' : 'btn-primary'}`}
            onClick={handleSubmit}
            disabled={savingFlow}
            title={inline ? (isDirty ? 'You have unsaved changes' : 'No changes to save') : undefined}
            style={inline && isDirty ? { boxShadow: '0 0 0 3px rgba(76,141,255,0.35)' } : undefined}
          >
            <Save size={16} />
            {savingFlow ? 'Making the input form…' : isNew ? 'Add' : inline && isDirty ? 'Save *' : 'Save'}
          </button>
        </div>
      </div>

      {/* Agent Stream Panel (Claude Code mode). Inline it floats as a fixed
          right-hand overlay so it doesn't disrupt the Details tab flow. */}
      {agentPanelOpen && generationMode === 'claude_code' && (
        <div style={inline ? {
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 400,
          background: 'var(--color-bg)',
          borderLeft: '1px solid var(--color-border)',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.2)',
          zIndex: 200,
          display: 'flex',
          flexDirection: 'column',
        } : {
          width: 400,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <AgentStreamPanel
            messages={agent.messages}
            isStreaming={agent.isStreaming}
            wsStatus={agent.wsStatus}
            error={agent.error}
            agentProgress={agent.agentProgress}
            onCancel={agent.cancelTask}
            onClear={agent.clearMessages}
            onClose={() => setAgentPanelOpen(false)}
            onPermissionResponse={agent.respondToPermission}
            title="Enhancing Specification"
          />
        </div>
      )}
    </div>
  )
}

/* ── Node overflow-menu item ─────────────────────────── */

function NodeMenuItem({ icon, label, onClick, danger, disabled }: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '8px 12px',
        fontSize: '0.8625rem',
        color: danger ? 'var(--color-error)' : 'var(--color-text)',
        background: 'transparent',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        textAlign: 'left',
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--color-bg)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      {icon}
      {label}
    </button>
  )
}

/* ── Screens tab pieces ──────────────────────────────── */

/** The object's own preview surface (where a click in the tree lands). */
function previewTabFor(node: FlatNavItem, modelExists: boolean):
  'details' | 'livepreview' | 'smartpage-livepreview' | 'site-livepreview' {
  if (node.obj_type === 'S') return 'smartpage-livepreview'
  if (node.obj_type === 'X') return 'site-livepreview'
  if (hasLivePreview(node.obj_type, modelExists)) return 'livepreview'
  return 'details'
}

const VIEW_MODE_OPTIONS: { mode: ObjectViewMode; label: string; Icon: typeof X }[] = [
  { mode: 'tab', label: 'Show in the page', Icon: LayoutPanelLeft },
  { mode: 'modal', label: 'Show in a dialog', Icon: AppWindow },
  { mode: 'fullscreen', label: 'Show full screen', Icon: Maximize2 },
]

/**
 * An object's Edit details or designer, shown the way the user picked: in the
 * page, in a dialog, or full screen. The bar carries the mode switcher (the
 * choice is remembered — objectViewStore) and Close, which goes back to the
 * preview. The element tree is the SAME in every mode — only styles change —
 * so switching mode never remounts the designer and loses unsaved work.
 * Dialog / full screen sit below the modal layer, so the unsaved-changes
 * prompt still shows on top.
 */
function ObjectView({ mode, onModeChange, icon, label, title, onClose, scroll = false, children }: {
  mode: ObjectViewMode
  onModeChange: (mode: ObjectViewMode) => void
  icon: React.ReactNode
  label: string
  title: string
  onClose: () => void
  /** The body scrolls (a form); otherwise the content manages its own scroll (a designer). */
  scroll?: boolean
  children: React.ReactNode
}) {
  const wrapperStyle: React.CSSProperties = mode === 'fullscreen'
    ? { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }
    : mode === 'modal'
      ? { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0, 0, 0, 0.5)' }
      : { flex: 1, minHeight: 460, display: 'flex', flexDirection: 'column' }
  const panelStyle: React.CSSProperties = mode === 'fullscreen'
    ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
    : mode === 'modal'
      ? {
          width: 'min(1280px, 100%)', height: 'min(90vh, 100%)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.3)',
        }
      : {
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
        }
  const overlay = mode !== 'tab'
  return (
    <div className={overlay ? 'swb' : undefined} style={wrapperStyle} data-tw-object-view={mode}>
      <div
        style={panelStyle}
        role={overlay ? 'dialog' : 'region'}
        aria-modal={overlay ? true : undefined}
        aria-label={`${label}: ${title}`}
      >
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, padding: '8px 12px 8px 16px', flexShrink: 0,
          background: 'var(--color-bg-secondary)', borderBottom: '1px solid var(--color-border)',
        }}>
          <span className="swb-chip swb-chip-accent" style={{ flex: 'none' }}>{icon} {label}</span>
          <strong style={{ flex: '1 1 120px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</strong>
          <div role="group" aria-label="How to show it" style={{ display: 'inline-flex', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', flex: 'none' }}>
            {VIEW_MODE_OPTIONS.map(({ mode: m, label: optLabel, Icon }, i) => (
              <button
                key={m}
                type="button"
                title={optLabel}
                aria-label={optLabel}
                aria-pressed={mode === m}
                data-tw-action={`view-mode-${m}`}
                onClick={() => { if (m !== mode) onModeChange(m) }}
                style={{
                  width: 34, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', borderLeft: i ? '1px solid var(--color-border)' : 'none', cursor: 'pointer',
                  background: mode === m ? 'var(--color-accent-weak)' : 'transparent',
                  color: mode === m ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                }}
              >
                <Icon size={15} />
              </button>
            ))}
          </div>
          <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={onClose}
            title="Close and go back to the preview" data-tw-action="close-object-view">
            <X size={14} /> Close
          </button>
        </div>
        <div style={scroll
          ? { flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px' }
          : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>
  )
}

function SpecView({ node, onBack, onEdit, onPlan }: {
  node: FlatNavItem
  onBack: () => void
  onEdit: () => void
  onPlan: () => void
}) {
  const spec = (node.functional_specs || '').trim()
  const isJson = spec.startsWith('{')
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 820 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span className="swb-eyebrow">Specification</span>
        <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={onBack}>
          <ChevronLeft size={14} /> Back to preview
        </button>
      </div>
      {node.description && <p style={{ margin: 0, fontSize: '1rem' }}>{node.description}</p>}
      {spec && !isJson ? (
        <div className="markdown-content" style={{ fontSize: '0.9375rem' }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{spec}</ReactMarkdown>
        </div>
      ) : (
        <p className="swb-muted" style={{ margin: 0 }}>
          {isJson
            ? 'This website’s settings (pages, menus, look) are edited in Details.'
            : 'No specification written for it yet.'}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingTop: 12, borderTop: '1px solid var(--color-surface-2)' }}>
        <span className="swb-muted" style={{ flex: '1 1 260px' }}>
          This is its part of your app’s plan. Changes you ask for update it here.
        </span>
        <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={onEdit}>Edit it</button>
        <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" onClick={onPlan}>See the whole plan</button>
      </div>
    </div>
  )
}

/** "See change history": saved versions (git commits) that touched this object's files. */
function ChangeHistoryModal({ project, node, onClose }: { project: string; node: FlatNavItem; onClose: () => void }) {
  const [commits, setCommits] = useState<Array<{ hash: string; short_hash: string; author: string; date: string; subject: string }> | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    // Model, layout and code files of one object all carry its name.
    const stem = node.obj_name.replace(/ /g, '_')
    getScmLog(project, 50, 0, [`*${stem}*`])
      .then((r) => { if (!cancelled) setCommits(r.commits || []) })
      .catch((err) => {
        console.error('[screens] change history failed', err)
        if (!cancelled) setError('The change history could not be loaded.')
      })
    return () => { cancelled = true }
  }, [project, node.obj_name])
  return (
    <Modal open onClose={onClose} title={`Change history — ${node.title || node.obj_name}`}>
      {error ? <p style={{ color: 'var(--color-error)' }}>{error}</p>
        : commits === null ? <div className="spinner" style={{ margin: '1.5rem auto' }} />
          : commits.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>
              No saved versions yet. Versions appear here once the app is committed to source control
              (Advanced › Source control).
            </p>
          ) : (
            <ul className="swb-list" style={{ maxHeight: 420, overflow: 'auto' }}>
              {commits.map((c) => (
                <li key={c.hash} style={{ padding: '10px 4px' }}>
                  <span style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column' }}>
                    <strong style={{ fontSize: 14 }}>{c.subject}</strong>
                    <span className="swb-muted">{c.author} · {new Date(c.date).toLocaleString()}</span>
                  </span>
                  <code style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{c.short_hash}</code>
                </li>
              ))}
            </ul>
          )}
    </Modal>
  )
}

/* ── Main PrototypeTab ───────────────────────────────── */

// In-memory stash of UI state that should survive a round-trip into a
// designer (transaction/dashboard/visual/smart-page) so the user returns
// to the same tab, the same expanded folders, and the same scroll
// positions they left behind. Keyed by project folder name so switching
// projects doesn't bleed state across them. Not persisted to storage —
// state should not leak across a hard reload or a new tab.
type PrototypeTabStash = {
  activeTab: 'details' | 'layout' | 'livepreview' | 'smartpage-livepreview' | 'site-livepreview' | 'objectcode' | 'spec'
  expandedIds: string[]
  treeScrollTop: number
  centerScrollTop: number
}
const prototypeStateStash = new Map<string, PrototypeTabStash>()

export default function PrototypeTab({ tab }: { tab?: JourneyTab } = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentProject, fetchProjects, selectProject, setSpecProgress, setModelProgress, setTypeProgress, setPrototypeLeaveGuard } = useProjectStore()
  const { user } = useAuth()
  const { canUpdateProperties, loaded: accessLoaded } = useProjectPermissions()
  const canEditProps = !accessLoaded || canUpdateProperties

  // Warm the module-level visual catalog as soon as the workbench shell mounts.
  // sanitizeVisualModel rebuilds a visual's layout from this catalog, so any
  // live-preview pane (visual/dashboard) opened before the Visual designer would
  // otherwise resolve against a cold cache and fall back to the Tabular grid.
  // loadVisualCatalog() is idempotent + cached, so this is a one-time fetch.
  useEffect(() => { void loadVisualCatalog() }, [])

  // Data
  const [appData, setAppData] = useState<RawAppData | null>(null)
  const [loading, setLoading] = useState(true)

  // Mobile nav drawer
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  // Nav tree state
  const [selectedNode, setSelectedNode] = useState<FlatNavItem | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  // Free-text filter for the navigation tree. The input is revealed by the
  // search icon next to the "Navigation" heading.
  const [navQuery, setNavQuery] = useState('')
  const [navSearchOpen, setNavSearchOpen] = useState(false)

  // Publish the selected nav node as the "open object" so the Ask-AI dock
  // can resolve "this" / "this screen" to it. Mirrors what
  // EndUserAppView does in Builder Mode — see builderContextStore.ts.
  // Cleared on deselect/unmount so a stale node never leaks into a later
  // agent request. Folders (obj_type === '-') don't count as an object.
  useEffect(() => {
    const { setOpenObject } = useBuilderContextStore.getState()
    if (selectedNode && selectedNode.obj_type && selectedNode.obj_type !== '-' && selectedNode.obj_name) {
      setOpenObject({
        obj_name: selectedNode.obj_name,
        obj_type: selectedNode.obj_type,
        title: selectedNode.title || selectedNode.obj_name,
      })
    } else {
      setOpenObject(null)
    }
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [selectedNode])

  // The menu order changed (drag-and-drop) and has not been saved yet.
  const [navDirty, setNavDirty] = useState(false)
  // Saves the in-memory menu (set below, once saveAppData exists) — used by
  // the leave guard so an unsaved add / drag / remove is never lost silently.
  const menuSaveRef = useRef<null | (() => Promise<boolean>)>(null)

  // Refs to the scroll containers — captured before navigating into a
  // designer (transaction/page/visual/dashboard) and reapplied on return
  // so the user lands on the same scroll position they left.
  const treeScrollRef = useRef<HTMLDivElement | null>(null)
  const centerScrollRef = useRef<HTMLDivElement | null>(null)

  // Modal state
  const [editingNode, setEditingNode] = useState<FlatNavItem | null>(null)
  const [isNewNode, setIsNewNode] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)

  // Objects added on this tab and not saved yet. The tree keeps them visible
  // even when the tab does not list their type (a screen added from an
  // analytics app's Dashboards), and the save says which tab now holds them.
  const [addedHere, setAddedHere] = useState<Set<string>>(() => new Set())
  const addedHereRef = useRef(addedHere)
  addedHereRef.current = addedHere
  const tabRef = useRef(tab)
  tabRef.current = tab

  // 3-dot overflow menu next to the type badge (Delete + code/model actions).
  const [nodeMenuOpen, setNodeMenuOpen] = useState(false)

  // Right-panel tab — 'details' shows the inline spec editor; 'layout'
  // embeds the full designer for the object type; 'livepreview' renders the
  // production-grade live runtime for transactions only. The choice persists
  // across node switches so an author scanning generated objects can keep a
  // tab pinned and just click through the tree.
  type RightTab = 'details' | 'layout' | 'livepreview' | 'smartpage-livepreview' | 'site-livepreview' | 'objectcode' | 'spec'
  const [activeTab, setActiveTab] = useState<RightTab>('details')

  // ── URL ↔ state sync (?obj=<nodeId>&view=<rightTab>) ──
  // The selected tree node and the right-panel tab are mirrored into the
  // query string so browser back/forward walks through selections and a
  // copied URL re-opens the exact same place in a new tab.
  const [searchParams, setSearchParams] = useSearchParams()
  // Search string we last wrote ourselves — while it's "in flight" (router
  // hasn't applied it yet) the URL→state effect must not treat the stale
  // params as an external navigation and clobber the state that produced it.
  const pendingUrlWriteRef = useRef<string | null>(null)
  // Blocks state→URL writes until the first URL→state pass after data load,
  // so a deep-linked ?obj=… isn't wiped by the initial null selection.
  const urlSyncReadyRef = useRef(false)
  // Set when the URL→state effect schedules setState calls: the state→URL
  // effect runs in the same flush with the not-yet-committed (stale) state
  // and must skip that one pass instead of writing the stale pair back.
  const skipNextStateWriteRef = useRef(false)

  // Object-scoped Code tab (transactions): which file the embedded source
  // editor should open. `nonce` lets the designer re-request the same path
  // after the user closed its editor tab.
  const [objectCodeOpen, setObjectCodeOpen] = useState<{ file: string; nonce: number } | null>(null)

  // The Code tab is NOT shown by default — it only appears once the author
  // explicitly opens code from the designer ("Open in code editor"). Closing
  // the tab hides it again and returns to the Design tab.
  const [codeTabOpen, setCodeTabOpen] = useState(false)

  // ── Embedded-designer unsaved-changes guard ──
  // The Design tab embeds a full designer; it reports its dirty state and
  // registers its save fn here so we can intercept tab/node switches and
  // offer Save & Continue / Discard / Keep Editing.
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorSaving, setEditorSaving] = useState(false)
  const [guardOpen, setGuardOpen] = useState(false)
  const editorSaveRef = useRef<null | (() => Promise<boolean>)>(null)
  const pendingNavRef = useRef<null | (() => void)>(null)

  const handleEditorDirtyChange = useCallback((dirty: boolean) => setEditorDirty(dirty), [])
  const handleRegisterEditorSave = useCallback((fn: () => Promise<boolean>) => {
    editorSaveRef.current = fn
  }, [])
  // An embedded designer just persisted its object. The Live Preview tab (and
  // the nav tree's status dot) are gated on the model EXISTING — a question
  // answered once, when the node was selected. Saving a brand-new object from
  // the Design tab is exactly when that answer changes, so re-ask it; without
  // this the tab stays missing until the node is reselected.
  const handleEditorSaved = useCallback(() => {
    const sel = selectedNodeRef.current
    const proj = projectRef.current
    if (proj && sel && sel.obj_type !== '-' && sel.obj_name) {
      checkModelStatus(proj, sel.obj_name, sel.obj_type)
        .then((res) => {
          if (selectedNodeRef.current?.obj_name !== sel.obj_name) return
          setModelExists(res.exists)
          setModelCheckedFor(sel.obj_name)
        })
        .catch(() => { /* keep the last good answer */ })
    }
    refreshBatchStatusesRef.current()
  }, [])

  // Run `action`, but if the embedded designer has unsaved edits, queue it
  // behind the discard/save prompt instead.
  const guardEditorNav = useCallback((action: () => void) => {
    if (activeTab === 'layout' && editorDirty) {
      pendingNavRef.current = action
      setGuardOpen(true)
    } else {
      action()
    }
  }, [activeTab, editorDirty])

  const runPendingNav = useCallback(() => {
    const a = pendingNavRef.current
    pendingNavRef.current = null
    setEditorDirty(false)
    if (a) a()
  }, [])

  const handleGuardSaveContinue = useCallback(async () => {
    setEditorSaving(true)
    let ok = true
    try {
      if (editorDirty && editorSaveRef.current) ok = await editorSaveRef.current()
      if (ok !== false && navDirty && menuSaveRef.current) ok = await menuSaveRef.current()
    } catch {
      ok = false
    } finally {
      setEditorSaving(false)
    }
    if (ok === false) return // save failed → keep the user on the page
    setGuardOpen(false)
    runPendingNav()
  }, [runPendingNav, editorDirty, navDirty])

  const handleGuardDiscard = useCallback(() => {
    setGuardOpen(false)
    runPendingNav()
  }, [runPendingNav])

  const handleGuardStay = useCallback(() => {
    setGuardOpen(false)
    pendingNavRef.current = null
  }, [])

  // ── Leaving the Prototype tab entirely ──
  // The in-tab guard above covers node/right-tab switches. To also catch
  // leaving the Prototype tab (clicking Overview / Specification / Advanced in
  // the top bar), we register a "leave guard" in the store while the embedded
  // designer is dirty. The top bar calls it with the real navigation as
  // `proceed`, and we route that through the SAME discard/save modal.
  // One stable callback, created once, rather than a fresh arrow each time the
  // effect runs. The store now ignores a re-register of the SAME guard, and it
  // can only tell it is the same if the identity holds — otherwise every
  // remount while dirty still writes to the store and can still drive the
  // remount loop it was meant to stop. Both values it closes over (a ref and a
  // setState) are already stable, so [] is honest here.
  const leaveGuard = useCallback((proceed: () => void) => {
    pendingNavRef.current = proceed
    setGuardOpen(true)
  }, [])

  useEffect(() => {
    if (!editorDirty && !navDirty) {
      setPrototypeLeaveGuard(null)
      return
    }
    setPrototypeLeaveGuard(leaveGuard)
    return () => setPrototypeLeaveGuard(null)
  }, [editorDirty, navDirty, leaveGuard, setPrototypeLeaveGuard])

  // Closing or reloading the browser tab with an unsaved menu asks first.
  useEffect(() => {
    if (!navDirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [navDirty])

  // Close the node overflow menu on any outside click.
  useEffect(() => {
    if (!nodeMenuOpen) return
    const close = () => setNodeMenuOpen(false)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [nodeMenuOpen])

  // Close the menu whenever the selected node changes so it never leaks.
  useEffect(() => {
    setNodeMenuOpen(false)
  }, [selectedNode?.id])

  // Action states
  const [modelExists, setModelExists] = useState(false)
  // obj_name the current modelExists value was fetched FOR. The tab-validity
  // coercion must not judge a node against another node's (or the default)
  // modelExists — otherwise a deep-linked ?view=livepreview is bounced to
  // Details before the async status check returns. Keyed by name (not a
  // boolean) so a stale value can never be mistaken for the current node's:
  // a plain "checked" flag reads true from the previous render's closure in
  // the very flush where the selection changes.
  const [modelCheckedFor, setModelCheckedFor] = useState<string | null>(null)
  const [generatingModel, setGeneratingModel] = useState(false)
  const [generatingDb, setGeneratingDb] = useState(false)
  const [deletingDb, setDeletingDb] = useState(false)
  const [saving, setSaving] = useState(false)

  // FRS / empty state
  const [specExists, setSpecExists] = useState<boolean | null>(null)
  const [frsContent, setFrsContent] = useState<string | null>(null)
  const [generatingApp, setGeneratingApp] = useState(false)

  // Batch model status for all objects
  const [objectStatuses, setObjectStatuses] = useState<Record<string, { model_exists: boolean }>>({})

  // Generate All state

  // Generation mode
  const [generationMode, setGenerationMode] = useState<string>('llm')
  const [effortConfig, setEffortConfig] = useState<Record<string, string>>({})

  const project = currentProject?.application.folder_name || ''

  // ── The journey's view of each object (status dot, Checked mark) ──
  const journey = useJourneyStore((st) => st.journey)
  const runAction = useJourneyAction()
  const setDockOpen = useDockUiStore((st) => st.setPanelOpen)
  const viewModes = useObjectViewStore((st) => st.modes)
  const setViewMode = useObjectViewStore((st) => st.setMode)
  const journeyByName = useMemo(() => {
    const m = new Map<string, JourneyObject>()
    for (const o of journey?.objects || []) m.set(`${o.obj_type}:${o.obj_name}`, o)
    return m
  }, [journey])
  const journeyObject = useCallback((n: FlatNavItem) => journeyByName.get(`${n.obj_type}:${n.obj_name}`) || null, [journeyByName])
  // Status from the journey; before it answers, the model check stands in.
  const statusOf = useCallback((n: FlatNavItem): ObjectStatus | null => {
    if (n.obj_type === '-') return null
    const j = journeyObject(n)
    if (j) return j.status
    if (n.obj_type === 'X') return 'to_check'
    return objectStatuses[n.obj_name]?.model_exists ? 'to_check' : 'planned'
  }, [journeyObject, objectStatuses])

  const [historyOpen, setHistoryOpen] = useState(false)
  // The test window: the test record, Run full test, Mark as checked.
  const [recordOpen, setRecordOpen] = useState(false)
  // The header's Make changes on a transaction: each request opens the mode
  // in the preview (LivePreviewTab acknowledges it with 0).
  const [makeChangesRequest, setMakeChangesRequest] = useState(0)
  // Website Make changes: the site preview opens in the mode
  // (`?make_changes=1`); bumped per press so the frame reloads into it.
  const [siteEditRequest, setSiteEditRequest] = useState(0)
  useEffect(() => { setSiteEditRequest(0) }, [selectedNode?.id])
  const { canEditObjects } = useProjectPermissions()

  // A click in the tree opens the object's PREVIEW once its model check is in
  // (a URL ?view= or a designer round-trip keeps the view it names).
  const wantPreviewRef = useRef(false)
  // A view picked from the ⋯ menu (Design, Details, Specification) is KEPT
  // while you click through the tree; null = follow each object's Preview.
  const pinnedViewRef = useRef<string | null>(null)

  // Load generation mode and thinking effort from settings
  const loadGenerationMode = useCallback(() => {
    getSettings().then((s) => {
      setGenerationMode(s.generation_mode || 'llm')
      if (s.thinking_effort_config) setEffortConfig(s.thinking_effort_config)
    }).catch(() => { /* use default */ })
  }, [])

  useEffect(() => {
    loadGenerationMode()
  }, [loadGenerationMode])

  // Claude Code agent for app generation
  const [agentAppPanelOpen, setAgentAppPanelOpen] = useState(false)
  const appAgent = useClaudeAgent({
    project,
    onComplete: async (changedFiles) => {
      const appFile = changedFiles.find((f) => f.path.includes('application.json'))
      if (appFile) {
        toast.success('Application generated by Claude Agent!')
        // Auto-save: rename Draft Application to the app's actual ID
        if (project === 'Draft Application') {
          try {
            const jsonData = await getJson(project)
            const appId = jsonData?.application?.id
            if (appId && appId !== 'Draft Application') {
              await saveDraft(appId)
              await fetchProjects()
              selectProject(appId)
              setGeneratingApp(false)
              setAgentAppPanelOpen(false)
              return
            }
          } catch { /* fallback below */ }
        }
        // Reload in place
        try {
          const data = normalizeAppData(await getJsonRaw(project))
          setAppData(data)
          const rootIds = new Set<string>(
            (data.navigation || [])
              .filter((n: FlatNavItem) => !n.parent_id)
              .map((n: FlatNavItem) => n.id)
          )
          setExpandedIds(rootIds)
        } catch { /* ignore */ }
      } else {
        toast.error('Application generation completed but no file was created')
      }
      setGeneratingApp(false)
      setAgentAppPanelOpen(false)
    },
  })

  // Claude Code agent for model generation
  const [agentModelPanelOpen, setAgentModelPanelOpen] = useState(false)
  const modelAgent = useClaudeAgent({
    project,
    onComplete: async (changedFiles) => {
      const hasModel = changedFiles.some((f) => f.path.includes('Metadata_Model'))
      if (hasModel && selectedNode) {
        try {
          await generateDatabase({
            obj_name: selectedNode.obj_name,
            obj_type: selectedNode.obj_type,
            project,
          })
        } catch {
          // post-processing failure is non-fatal
        }
        toast.success('Model generated by Claude Agent!')
        setModelExists(true)
        refreshBatchStatuses()
      } else {
        toast.error('Model generation completed but no file was created')
      }
      setGeneratingModel(false)
      setAgentModelPanelOpen(false)
    },
  })

  // Reset generatingModel when agent encounters a fatal error
  useEffect(() => {
    if (modelAgent.error && generatingModel) {
      setGeneratingModel(false)
    }
  }, [modelAgent.error, generatingModel])

  // Load raw app data
  useEffect(() => {
    if (!project) return
    setLoading(true)
    setSpecExists(null)
    setFrsContent(null)
    // Fresh project load → restart the URL↔state handshake from scratch.
    urlSyncReadyRef.current = false
    pendingUrlWriteRef.current = null
    skipNextStateWriteRef.current = false
    getJsonRaw(project)
      .then((raw) => {
        const data = normalizeAppData(raw)
        setAppData(data)
        // Auto-expand root items
        const rootIds = new Set<string>(
          (data.navigation || [])
            .filter((n: FlatNavItem) => !n.parent_id)
            .map((n: FlatNavItem) => n.id)
        )
        setExpandedIds(rootIds)
        // Restore selected node from navigation state (e.g. returning from model editor)
        const restoreNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
        if (restoreNodeId) {
          const node = (data.navigation || []).find((n: FlatNavItem) => n.id === restoreNodeId)
          if (node) {
            setSelectedNode(node)
            // Expand all ancestors so the node is visible
            const ancestorIds = new Set<string>()
            let current = node
            while (current && current.parent_id) {
              if (ancestorIds.has(current.parent_id)) break
              ancestorIds.add(current.parent_id)
              current = (data.navigation || []).find((n: FlatNavItem) => n.id === current.parent_id)!
              if (!current) break
            }
            if (ancestorIds.size > 0) {
              setExpandedIds((prev) => new Set([...prev, ...ancestorIds]))
            }
          }
          // Apply the stashed UI state (active tab + expanded folders +
          // scroll positions). We pop the stash so a project switch later
          // doesn't replay it, and we restrict expanded restoration to
          // ids that still exist in the freshly-loaded navigation. The
          // scroll positions can't be applied synchronously — the DOM
          // for the right pane isn't laid out until the selected-node
          // branch below mounts, so defer to a rAF.
          const stash = prototypeStateStash.get(project)
          if (stash) {
            prototypeStateStash.delete(project)
            setActiveTab(stash.activeTab)
            if (stash.expandedIds.length > 0) {
              const validIds = new Set(
                (data.navigation || []).map((n: FlatNavItem) => n.id),
              )
              const restored = stash.expandedIds.filter((id) => validIds.has(id))
              setExpandedIds((prev) => new Set([...prev, ...restored]))
            }
            requestAnimationFrame(() => {
              if (treeScrollRef.current) treeScrollRef.current.scrollTop = stash.treeScrollTop
              if (centerScrollRef.current) centerScrollRef.current.scrollTop = stash.centerScrollTop
            })
          }
          // Seed the URL with the restored selection so the URL↔state sync
          // effects start from a consistent pair — otherwise the first sync
          // pass would read the param-less URL and deselect the node we just
          // restored.
          const seeded = new URLSearchParams(window.location.search)
          if (node) seeded.set('obj', node.id)
          else seeded.delete('obj')
          const seedView = stash?.activeTab ?? 'details'
          if (seedView !== 'details') seeded.set('view', seedView)
          else seeded.delete('view')
          if (seeded.toString() !== window.location.search.replace(/^\?/, '')) {
            pendingUrlWriteRef.current = seeded.toString()
            setSearchParams(seeded, { replace: true })
          }
          // Clear the state so refreshing doesn't re-select
          window.history.replaceState({}, '')
        }
      })
      .catch(async () => {
        setAppData(null)
        // Check if specification exists
        try {
          const frsData = await getFrsContent(project)
          if (frsData.content) {
            setSpecExists(true)
            setFrsContent(frsData.content)
          } else {
            setSpecExists(false)
          }
        } catch {
          setSpecExists(false)
        }
      })
      .finally(() => setLoading(false))
  }, [project])

  // URL → state: applies ?obj/?view to the tree selection and right-panel
  // tab. Handles deep links pasted into a fresh tab (runs once appData is
  // available) and browser back/forward (runs on every params change).
  // Deliberately bypasses the unsaved-changes guard: by the time popstate
  // fires the URL has already moved, so blocking here would desync them —
  // in-app clicks still go through guardEditorNav before touching state.
  useEffect(() => {
    if (!appData) return
    const cur = searchParams.toString()
    if (pendingUrlWriteRef.current !== null) {
      // Echo of our own write — consume it, don't re-apply.
      if (pendingUrlWriteRef.current === cur) {
        pendingUrlWriteRef.current = null
        urlSyncReadyRef.current = true
      }
      return
    }
    const objParam = searchParams.get('obj')
    if ((objParam ?? null) !== (selectedNode?.id ?? null)) {
      if (objParam) {
        const node = appData.navigation.find((n) => n.id === objParam)
        if (node) {
          skipNextStateWriteRef.current = true
          setSelectedNode(node)
          // Expand all ancestors so the deep-linked node is visible
          const ancestorIds = new Set<string>()
          let current = node
          while (current && current.parent_id) {
            if (ancestorIds.has(current.parent_id)) break
            ancestorIds.add(current.parent_id)
            current = appData.navigation.find((n) => n.id === current.parent_id)!
            if (!current) break
          }
          if (ancestorIds.size > 0) {
            setExpandedIds((prev) => new Set([...prev, ...ancestorIds]))
          }
        }
        // Unknown id (stale link / node deleted) → keep current selection.
      } else {
        skipNextStateWriteRef.current = true
        setSelectedNode(null)
      }
    }
    const viewParam = (searchParams.get('view') || 'details') as RightTab
    const RIGHT_TABS: RightTab[] = ['details', 'layout', 'livepreview', 'smartpage-livepreview', 'site-livepreview', 'objectcode', 'spec']
    if (RIGHT_TABS.includes(viewParam) && viewParam !== activeTab) {
      // The tab-validity effect below still coerces views the node's type
      // doesn't support, so a mismatched ?view can't strand the panel.
      skipNextStateWriteRef.current = true
      setActiveTab(viewParam)
    }
    urlSyncReadyRef.current = true
    // selectedNode/activeTab are read for comparison only — re-running on
    // their change is the job of the state→URL effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, appData])

  // State → URL: mirror the selection + right tab into the query string.
  // Node changes push (so back/forward steps through them); view-only
  // changes replace (tab flips shouldn't spam history).
  useEffect(() => {
    if (!appData || !urlSyncReadyRef.current) return
    if (skipNextStateWriteRef.current) {
      // This pass still sees the pre-application state from the URL→state
      // effect above — once that state commits, this effect re-runs and the
      // params will already match, so consuming the flag here is enough.
      skipNextStateWriteRef.current = false
      return
    }
    const next = new URLSearchParams(searchParams)
    if (selectedNode?.id) next.set('obj', selectedNode.id)
    else next.delete('obj')
    if (activeTab !== 'details') next.set('view', activeTab)
    else next.delete('view')
    const nextStr = next.toString()
    if (nextStr !== searchParams.toString()) {
      pendingUrlWriteRef.current = nextStr
      const objChanged = (searchParams.get('obj') ?? null) !== (selectedNode?.id ?? null)
      setSearchParams(next, { replace: !objChanged })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id, activeTab, appData])

  const handleGenerateAppFromFrs = useCallback(async () => {
    if (!frsContent) return

    // Claude Code Agent mode
    if (generationMode === 'claude_code') {
      setGeneratingApp(true)
      setAgentAppPanelOpen(true)
      appAgent.startTask('app_json', {
        description: 'Generate application JSON from FRS',
        effort: effortConfig.app_json,
      })
      return
    }

    // LLM API mode (existing behavior)
    setGeneratingApp(true)
    try {
      await generateAppFromFrs(frsContent, project)
      // Auto-save: rename Draft Application to the app's actual ID
      if (project === 'Draft Application') {
        const jsonData = await getJson(project)
        const appId = jsonData?.application?.id
        if (appId && appId !== 'Draft Application') {
          await saveDraft(appId)
          toast.success('Application generated!')
          await fetchProjects()
          selectProject(appId)
          return
        }
      }
      // Fallback: reload in place
      toast.success('Application generated!')
      const data = normalizeAppData(await getJsonRaw(project))
      setAppData(data)
      const rootIds = new Set<string>(
        (data.navigation || [])
          .filter((n: FlatNavItem) => !n.parent_id)
          .map((n: FlatNavItem) => n.id)
      )
      setExpandedIds(rootIds)
    } catch {
      toast.error('Failed to generate application')
    } finally {
      setGeneratingApp(false)
    }
  }, [frsContent, project, fetchProjects, selectProject, generationMode, appAgent])

  // Check model status when a node is selected
  useEffect(() => {
    if (!selectedNode || selectedNode.obj_type === '-' || !project) {
      setModelExists(false)
      setModelCheckedFor(null)
      wantPreviewRef.current = false
      return
    }
    const checkedName = selectedNode.obj_name
    const node = selectedNode
    checkModelStatus(project, selectedNode.obj_name, selectedNode.obj_type)
      .then((res) => {
        setModelExists(res.exists)
        setModelCheckedFor(checkedName)
        if (wantPreviewRef.current) {
          wantPreviewRef.current = false
          setActiveTab(previewTabFor(node, res.exists))
        }
      })
      .catch(() => {
        setModelExists(false)
        setModelCheckedFor(checkedName)
        wantPreviewRef.current = false
      })
  }, [selectedNode, project])

  // A pending "open this code file" request belongs to one object — drop it
  // (and close the per-object Code tab) when the user moves to another node.
  useEffect(() => {
    setObjectCodeOpen(null)
    setCodeTabOpen(false)
  }, [selectedNode?.obj_name])

  // Migrate the right-panel tab when the new node's type doesn't support
  // the current tab. The two Live Preview tabs (`livepreview` for T,
  // `smartpage-livepreview` for S) are kind-specific — without this,
  // switching from a smart-page Live Preview to a transaction node leaves
  // `activeTab` stuck on `smartpage-livepreview`, which renders nothing
  // (the conditional rendering branches all require both tab + obj_type
  // to line up). Map preview→equivalent-preview when possible, fall back
  // to Details otherwise.
  useEffect(() => {
    if (!selectedNode) return
    // Don't judge validity against a stale modelExists — wait until the
    // status check for THIS node has resolved so livepreview/objectcode
    // aren't bounced to Details during the async gap (matters for ?view=
    // deep links). Folders never have a model, no need to wait.
    if (selectedNode.obj_type !== '-' && modelCheckedFor !== selectedNode.obj_name) return
    // 'layout' (the embedded designer) exists for every type that has a
    // designer; other surfaces are type-specific.
    const validTabs: RightTab[] = ['details', 'spec']
    if (NODE_HAS_DESIGNER.includes(selectedNode.obj_type)) validTabs.push('layout')
    if (hasLivePreview(selectedNode.obj_type, modelExists)) validTabs.push('livepreview')
    if (selectedNode.obj_type === 'S') validTabs.push('smartpage-livepreview')
    if (selectedNode.obj_type === 'X') validTabs.push('site-livepreview')
    if ((selectedNode.obj_type === 'T' || selectedNode.obj_type === 'S') && modelExists && codeTabOpen) validTabs.push('objectcode')
    if (validTabs.includes(activeTab)) return
    // Current tab isn't valid for the new node — prefer that node's
    // equivalent preview surface (so leaving a layout/preview tab lands
    // on the matching preview), else fall back to Details.
    if (hasLivePreview(selectedNode.obj_type, modelExists)) {
      setActiveTab('livepreview')
      return
    }
    if (selectedNode.obj_type === 'S') {
      setActiveTab('smartpage-livepreview')
      return
    }
    setActiveTab('details')
  }, [selectedNode, modelExists, modelCheckedFor, activeTab, codeTabOpen])

  // Load batch model statuses + compute spec/model progress when appData changes
  useEffect(() => {
    if (!appData || !project) {
      setObjectStatuses({})
      setSpecProgress(null)
      setModelProgress(null)
      setTypeProgress([])
      return
    }
    // Same loader the workbench Overview uses — both write the same store, so
    // computing it two ways made the numbers shift when you switched tabs.
    let cancelled = false
    const nonFolderItems = appData.navigation.filter((n) => n.obj_type !== '-')
    loadProjectProgress(project, nonFolderItems)
      .then((progress) => {
        if (cancelled) return
        setObjectStatuses(progress.statuses)
        setSpecProgress(progress.specPct)
        setModelProgress(progress.modelPct)
        setTypeProgress(progress.typeProgress)
      })
      .catch(() => {
        if (cancelled) return
        setObjectStatuses({})
        setSpecProgress(null)
        setModelProgress(null)
        setTypeProgress([])
      })
    return () => { cancelled = true }
  }, [appData, project, setSpecProgress, setModelProgress, setTypeProgress])

  // Refresh batch model statuses (after a save / agent edit)
  const refreshBatchStatuses = useCallback(() => {
    if (!appData || !project) return
    const nonFolderItems = appData.navigation.filter((n) => n.obj_type !== '-')
    loadProjectProgress(project, nonFolderItems)
      .then((progress) => {
        setObjectStatuses(progress.statuses)
        setModelProgress(progress.modelPct)
        setTypeProgress(progress.typeProgress)
      })
      .catch(() => { /* ignore — the panel keeps its last good numbers */ })
  }, [appData, project, setModelProgress, setTypeProgress])

  // Live-refresh after the Ask-AI dock (or any agent flow) reports it
  // touched project files. Without this the user has to hard-refresh the
  // browser to see model/page edits, navigation additions, etc. — the
  // preview panes (embedded designers, LivePreviewLazyPane, etc.) already
  // listen for this event, but the surrounding workbench shell (nav tree
  // status dots, model-exists gate for the Live Preview tab, spec/FRS
  // tab) was reading stale state. Cheap to over-refresh; expensive (UX
  // wise) to under-refresh.
  //
  // Refs keep the listener identity stable so we don't re-attach on every
  // render — the handler always reads the latest selectedNode/project.
  const selectedNodeRef = useRef(selectedNode)
  useEffect(() => { selectedNodeRef.current = selectedNode }, [selectedNode])
  const projectRef = useRef(project)
  useEffect(() => { projectRef.current = project }, [project])
  const refreshBatchStatusesRef = useRef(refreshBatchStatuses)
  useEffect(() => { refreshBatchStatusesRef.current = refreshBatchStatuses }, [refreshBatchStatuses])

  useEffect(() => {
    const onFilesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail as { project?: string; files?: string[] } | undefined
      const evProj = detail?.project
      const curProj = projectRef.current
      if (!curProj) return
      // Ignore events for other projects so background tabs/workspaces
      // don't trigger spurious refetches here.
      if (evProj && evProj !== curProj) return

      // 1) Reload application.json — covers navigation adds/removes/renames
      //    done by the agent (or by direct file edits).
      getJsonRaw(curProj)
        .then((raw) => {
          const data = normalizeAppData(raw)
          setAppData(data)
        })
        .catch(() => { /* keep old state on failure */ })

      // 2) Re-check the selected node's model — this drives the
      //    Live Preview tab gate (modelExists) and the green dot in the
      //    nav tree's status indicator.
      const sel = selectedNodeRef.current
      if (sel && sel.obj_type && sel.obj_type !== '-' && sel.obj_name) {
        checkModelStatus(curProj, sel.obj_name, sel.obj_type)
          .then((res) => setModelExists(res.exists))
          .catch(() => { /* ignore */ })
      }

      // 3) Refresh batch statuses so all nav-tree dots reflect new state.
      refreshBatchStatusesRef.current()
    }
    window.addEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
    return () =>
      window.removeEventListener('twasta:agent-files-changed', onFilesChanged as EventListener)
  }, [])

  // `?add=1` (the Next step's "Add a screen myself") opens the Add form once.
  useEffect(() => {
    if (!appData || searchParams.get('add') !== '1') return
    const next = new URLSearchParams(searchParams)
    next.delete('add')
    setSearchParams(next, { replace: true })
    handleAddRootNode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData, searchParams])

  // Save the entire appData to server
  const saveAppData = useCallback(
    async (data: RawAppData) => {
      setSaving(true)
      try {
        await saveJson(project, data)
        setNavDirty(false)
        const types = tabRef.current?.types
        const added = addedHereRef.current
        const elsewhere = types && added.size
          ? data.navigation.filter((n) => added.has(n.id) && !types.includes(n.obj_type))
          : []
        setAddedHere(new Set())
        if (!elsewhere.length) {
          toast.success('Application saved')
          refreshJourney()
          return true
        }
        // Re-read the journey FIRST: the tab that lists these may only now
        // exist (analytics' Data entry appears with the first screen).
        await useJourneyStore.getState().refresh(project)
        announceElsewhere(elsewhere)
        return true
      } catch (err) {
        // A structural refusal (e.g. a folder saved with an object type) names
        // the node and the fix — show it rather than a bare failure.
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        toast.error(typeof detail === 'string' && detail ? detail : 'Failed to save application')
        return false
      } finally {
        setSaving(false)
      }
    },
    [project]
  )
  useEffect(() => {
    menuSaveRef.current = appData ? () => saveAppData(appData) : null
  }, [appData, saveAppData])

  // "Saved — it's under Data entry": the first tab of the journey that lists
  // the type, so the same words serve every app type.
  function announceElsewhere(nodes: FlatNavItem[]) {
    const tabs = useJourneyStore.getState().journey?.tabs || []
    const here = tabRef.current?.id
    const homeOf = (type: string) => tabs.find(
      (t) => t.view === 'objects' && t.id !== here && (!t.types || t.types.includes(type)),
    )
    const first = nodes[0]
    const home = homeOf(first.obj_type)
    const typeName = (vocabulary.typeNames as Record<string, string>)[first.obj_type] || 'This item'
    const what = nodes.length === 1 ? `"${first.title || first.obj_name || typeName}"` : `${nodes.length} items`
    if (!home) {
      toast.success(`Saved. ${what} ${nodes.length === 1 ? 'is' : 'are'} not listed on this tab.`, { duration: 6000 })
      return
    }
    toast((t) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        Saved. {what} {nodes.length === 1 ? 'is' : 'are'} under {home.label}.
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => { toast.dismiss(t.id); void runAction(`tab:${home.id}`) }}
        >
          Open {home.label}
        </button>
      </span>
    ), { duration: 8000 })
  }

  // Drag and drop lives in AppTree; a drop hands back the re-ordered menu,
  // kept in memory until Save (exactly as before).
  const handleMove = useCallback((navigation: FlatNavItem[]) => {
    setAppData((prev) => (prev ? { ...prev, navigation } : prev))
    setNavDirty(true)
  }, [])

  const handleAutoExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      if (prev.has(id)) return prev
      return new Set([...prev, id])
    })
  }, [])

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Node CRUD
  const handleAddRootNode = () => {
    if (!appData) return
    // "Add screen" opens the same details form Add Node did; it starts on the
    // tab's own kind of object (Folder is still one pick away in the form).
    const addType = tab?.add_type || '-'
    const newNode: FlatNavItem = {
      id: generateId(appData.navigation, ''),
      title: '',
      parent_id: '',
      icon_image: addType === '-' ? 'folder' : 'description',
      description: '',
      obj_name: '',
      obj_type: addType,
      menu_path: '',
      functional_specs: '',
      order: appData.navigation.filter((n) => !n.parent_id).length,
    }
    setEditingNode(newNode)
    setIsNewNode(true)
    setEditModalOpen(true)
  }

  const handleAddChildNode = () => {
    if (!appData || !selectedNode) return
    const newNode: FlatNavItem = {
      id: generateId(appData.navigation, selectedNode.id),
      title: '',
      parent_id: selectedNode.id,
      icon_image: 'description',
      description: '',
      obj_name: '',
      // The tab's own kind (a dashboard inside a Dashboards folder).
      obj_type: tab?.add_type || 'T',
      menu_path: '',
      functional_specs: '',
      order: getChildren(appData.navigation, selectedNode.id).length,
    }
    setEditingNode(newNode)
    setIsNewNode(true)
    setEditModalOpen(true)
  }

  const handleDeleteNode = () => {
    if (!appData || !selectedNode) return
    const hasKids = appData.navigation.some((n) => n.parent_id === selectedNode.id)
    if (!window.confirm(
      `Remove "${selectedNode.title || selectedNode.obj_name}" from the app${hasKids ? ' — with everything inside it' : ''}?\n\n`
      + 'It leaves the menu when you Save. Its design files are kept.',
    )) return
    // Collect all descendant IDs
    const toDelete = new Set<string>()
    const collectDescendants = (id: string) => {
      toDelete.add(id)
      appData.navigation
        .filter((n) => n.parent_id === id)
        .forEach((n) => collectDescendants(n.id))
    }
    collectDescendants(selectedNode.id)

    // A deleted page leaves every website menu that listed it (the backend's
    // nav_edit.remove_node does the same for programmatic deletes).
    const deletedPages = new Set(
      appData.navigation
        .filter((n) => toDelete.has(n.id) && n.obj_type === 'S' && n.obj_name)
        .map((n) => n.obj_name),
    )
    const nav = appData.navigation
      .filter((n) => !toDelete.has(n.id))
      .map((n) => {
        if (n.obj_type !== 'X' || !deletedPages.size) return n
        const spec = parseWebsiteSpec(n.functional_specs)
        const menus = spec.menus ? stripPagesFromMenus(spec.menus, deletedPages) : null
        return menus ? { ...n, functional_specs: JSON.stringify({ ...spec, menus }, null, 2) } : n
      })
    setAppData({ ...appData, navigation: nav })
    setSelectedNode(null)
    setNavDirty(true)
    toast.success('Removed — press Save to keep the change')
  }

  // Convert a website from the legacy menu-folder convention to named menus.
  // The conversion moves nodes and rewrites the site's nav_menu blocks on the
  // SERVER, so the tree is saved first (nothing unsaved is lost or
  // overwritten), then reloaded and the website re-selected.
  const [formEpoch, setFormEpoch] = useState(0)
  const handleConvertMenus = useCallback(async (siteId: string) => {
    if (!appData) return
    try {
      await saveJson(project, appData)
      const summary = await convertSiteMenus(project, siteId)
      const data = normalizeAppData(await getJsonRaw(project))
      setAppData(data)
      const site = data.navigation.find((n: FlatNavItem) => n.id === siteId)
      if (site) setSelectedNode(site)
      setFormEpoch((e) => e + 1)
      const made = Object.entries(summary.menus).map(([k, v]) => `${k} (${v})`).join(', ')
      toast.success(made ? `Menus created: ${made}` : 'Nothing to convert')
    } catch (err) {
      console.error('[site-menus] conversion failed', err)
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(`Could not convert the menus${detail ? `: ${detail}` : ''}`)
      throw err
    }
  }, [appData, project])

  const handleSaveNode = (node: FlatNavItem) => {
    if (!appData) return
    let nav = [...appData.navigation]
    if (isNewNode) {
      nav.push(node)
      setAddedHere((prev) => new Set(prev).add(node.id))
      // Expand parent so the new child is visible
      if (node.parent_id) {
        setExpandedIds((prev) => new Set(prev).add(node.parent_id))
      }
    } else {
      nav = nav.map((n) => (n.id === node.id ? node : n))
      // Update the selected node reference
      if (selectedNode?.id === node.id) {
        setSelectedNode(node)
      }
    }
    setAppData({ ...appData, navigation: nav })
    setNavDirty(true)
    setEditModalOpen(false)
    setEditingNode(null)
  }

  // Inline Details "Save" — like handleSaveNode but persists immediately to
  // disk (the Details tab is a real editor now, so its Save must stick) and
  // keeps the user on the current node.
  const handleSaveNodeInline = (node: FlatNavItem) => {
    if (!appData) return
    const nav = appData.navigation.map((n) => (n.id === node.id ? node : n))
    const updated = { ...appData, navigation: nav }
    setAppData(updated)
    setSelectedNode(node)
    saveAppData(updated)
  }

  const handleSaveProperties = (app: RawAppData['application']) => {
    if (!appData) return
    const updated = { ...appData, application: app }
    setAppData(updated)
    saveAppData(updated)
  }

  // Action handlers
  // ── Import a report (obj_type "R") from JRXML ──────────────────
  // A report that already exists as a Jasper design has nothing to generate:
  // the files ARE the model. Reading them here writes the model + its
  // Report_Design sidecars straight to project storage, so the node goes from
  // "no model" to a report the designer and Live Preview can open.
  const reportImportRef = useRef<HTMLInputElement>(null)
  const [importingReport, setImportingReport] = useState(false)
  const handleImportReport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || [])
    e.target.value = ''
    if (!picked.length || !selectedNode || !project) return
    if (modelExists && !window.confirm(
      `A model already exists for "${selectedNode.obj_name}". Importing replaces its JRXML files. Continue?`,
    )) return
    const objName = selectedNode.obj_name
    setImportingReport(true)
    try {
      const read = await readPickedReportFiles(picked)
      // Keep whatever the object already carries (output formats, connection,
      // an imported report's LEGACY_SOURCE) — only the design changes.
      let base = blankReportModel(objName)
      if (modelExists) {
        try {
          const existing = await loadReportObject(project, objName)
          if (existing.content) base = sanitizeReportModel(existing.content, objName)
        } catch {
          // Unreadable model — import over a blank one rather than refuse.
        }
      }
      const res = await saveReportObject(project, objName, {
        model: applyImportedFiles(base, read),
        jrxml_files: read.jrxmlFiles,
      })
      for (const w of res.warnings || []) toast(w, { icon: '⚠️' })
      toast.success(
        read.files.length === 1
          ? `Imported ${read.main.filename}`
          : `Imported ${read.files.length} files (main: ${read.main.filename})`,
      )
      setModelExists(true)
      refreshBatchStatuses()
    } catch (err) {
      console.error('Report import failed', err)
      toast.error(err instanceof Error ? err.message : 'Failed to import report files')
    } finally {
      setImportingReport(false)
    }
  }

  const handleGenerateModel = async () => {
    if (!selectedNode || !project) {
      toast.error('Please select an activity node first')
      return
    }

    if (modelExists) {
      const ok = window.confirm(
        `A model already exists for "${selectedNode.obj_name}". Regenerating will discard the existing model and all changes will be lost. Continue?`
      )
      if (!ok) return
    }

    // Claude Code Agent mode — dispatch to the dedicated task_type per
    // obj_type so each pipeline (transaction / visual / page / schema) uses
    // its own prompt module and produces the correct JSON shape.
    //
    // A Smart Page with "clone reference URL" enabled FORCES the agent path
    // even in LLM-API mode — the static page_generator can't drive WebFetch /
    // Bash / vision, which the clone flow requires.
    const forceAgentForClone =
      selectedNode.obj_type === 'S'
      && !!selectedNode.clone_reference
      && !!selectedNode.design_reference_url
    if (generationMode === 'claude_code' || forceAgentForClone) {
      setGeneratingModel(true)
      setAgentModelPanelOpen(true)
      const agentTaskType =
        selectedNode.obj_type === 'V'
          ? 'visual'
          : selectedNode.obj_type === 'S'
          ? 'page'
          : selectedNode.obj_type === 'M'
          ? 'schema'
          : selectedNode.obj_type === 'D'
          ? 'dashboard'
          : selectedNode.obj_type === 'R'
          ? 'report'
          : 'model'
      try {
        await modelAgent.startTask(agentTaskType, {
          obj_name: selectedNode.obj_name,
          obj_type: selectedNode.obj_type,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
          project_folder: project,
          effort: effortConfig.model,
          design_reference_url: selectedNode.design_reference_url,
          clone_reference: !!selectedNode.clone_reference,
        })
      } catch (err) {
        console.error('Agent model generation failed:', err)
        toast.error('Failed to start agent task')
        setGeneratingModel(false)
        setAgentModelPanelOpen(false)
      }
      return
    }

    // LLM API mode — three completely independent pipelines.
    // Visual / Smart Page / Transaction each have their own service +
    // endpoint and produce different JSON shapes. Branch up front so no
    // code from one pipeline ever runs against another's object.
    setGeneratingModel(true)
    try {
      if (selectedNode.obj_type === 'M') {
        await generateVisualSchemaForNode({
          project,
          obj_name: selectedNode.obj_name,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
        })
        toast.success('Schema generated successfully')
        setModelExists(true)
        refreshBatchStatuses()
      } else if (selectedNode.obj_type === 'V') {
        await generateVisualObject({
          project,
          obj_name: selectedNode.obj_name,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
        })
        toast.success('Visual generated successfully')
        setModelExists(true)
        refreshBatchStatuses()
      } else if (selectedNode.obj_type === 'D') {
        await generateDashboardObject({
          project,
          obj_name: selectedNode.obj_name,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
        })
        toast.success('Dashboard generated successfully')
        setModelExists(true)
        refreshBatchStatuses()
      } else if (selectedNode.obj_type === 'S') {
        await generatePageObject({
          project,
          obj_name: selectedNode.obj_name,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
          design_reference_url: selectedNode.design_reference_url,
        })
        toast.success('Page generated successfully')
        setModelExists(true)
        refreshBatchStatuses()
      } else if (selectedNode.obj_type === 'R') {
        await generateReportObject({
          project,
          obj_name: selectedNode.obj_name,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
        })
        toast.success('Report generated successfully')
        setModelExists(true)
        refreshBatchStatuses()
      } else {
        // Transaction-class: T / G — write model + auto-generate database
        await generateModel({
          obj_name: selectedNode.obj_name,
          obj_type: selectedNode.obj_type,
          description: selectedNode.description,
          functional_specs: selectedNode.functional_specs,
          project_folder: project,
        })
        try {
          await generateDatabase({
            obj_name: selectedNode.obj_name,
            obj_type: selectedNode.obj_type,
            project,
          })
        } catch { /* code generation failure is non-fatal */ }
        toast.success('Model generated successfully')
        setModelExists(true)
        refreshBatchStatuses()
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Failed to generate model')
      console.error('Model generation failed:', err)
    } finally {
      setGeneratingModel(false)
    }
  }

  // Snapshot the UI bits we want to restore when the user returns from a
  // designer. selectedNode is already handled via location.state — this
  // covers everything else (active right-panel tab, expanded folders,
  // scroll positions on both panes).
  const stashPrototypeState = useCallback(() => {
    if (!project) return
    prototypeStateStash.set(project, {
      activeTab,
      expandedIds: Array.from(expandedIds),
      treeScrollTop: treeScrollRef.current?.scrollTop ?? 0,
      centerScrollTop: centerScrollRef.current?.scrollTop ?? 0,
    })
  }, [project, activeTab, expandedIds])

  const handleGenerateDatabase = async () => {
    if (!selectedNode || !project) return
    setGeneratingDb(true)
    try {
      await generateDatabase({
        obj_name: selectedNode.obj_name,
        obj_type: selectedNode.obj_type,
        project,
      })
      toast.success('Database generated successfully')
    } catch {
      toast.error('Failed to generate database')
    } finally {
      setGeneratingDb(false)
    }
  }

  const handleDeleteDatabase = async () => {
    if (!selectedNode || !project) return
    setDeletingDb(true)
    try {
      await deleteDatabase({
        obj_name: selectedNode.obj_name,
        obj_type: selectedNode.obj_type,
        project,
      })
      toast.success('Database codes deleted')
    } catch {
      toast.error('Failed to delete database')
    } finally {
      setDeletingDb(false)
    }
  }

  const handleDownloadModel = () => {
    if (!selectedNode || !project) return
    const url = downloadModel(project, selectedNode.obj_type, selectedNode.obj_name)
    window.open(url, '_blank')
  }

  const handleDesignWorkflow = () => {
    if (!selectedNode || !project) return
    stashPrototypeState()
    // "Design Workflow" is a transactions-only action, so auto-link the
    // Workflow Designer's approval binding to this transaction (?bind=…) — the
    // user shouldn't have to bind it again on arrival.
    const bind = selectedNode.obj_type === 'T'
      ? `?bind=${encodeURIComponent(selectedNode.obj_name)}`
      : ''
    navigate(
      `/designers/workflow/${encodeURIComponent(project)}/${encodeURIComponent(selectedNode.obj_name)}${bind}`,
      { state: { selectedNodeId: selectedNode.id } }
    )
  }

  const handleSaveAll = () => {
    if (!appData) return
    saveAppData(appData)
  }

  // Loading / empty states
  if (!currentProject) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>Select a project to view.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
      </div>
    )
  }

  if (!appData) {
    return (
      <div style={{ display: 'flex', gap: '1rem', flex: 1 }}>
        <div className="card" style={{ textAlign: 'center', padding: '3rem', flex: 1 }}>
          {specExists === null ? (
            <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
          ) : specExists ? (
            <>
              <FileText size={48} style={{ color: 'var(--color-accent)', opacity: 0.7, margin: '0 auto 1rem' }} />
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                Specification is ready. Generate the application to view the preview.
              </p>
              <button
                className="btn btn-primary"
                onClick={handleGenerateAppFromFrs}
                disabled={generatingApp}
              >
                {generatingApp ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Play size={16} />
                )}
                {generatingApp ? 'Generating...' : 'Generate Application'}
              </button>
            </>
          ) : (
            <>
              <span
                className="material-icons"
                style={{ fontSize: 49, color: 'var(--color-text-secondary)', marginBottom: '1rem', display: 'block' }}
              >
                preview
              </span>
              <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
                No specification found. Create a specification first.
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/generateapp')}>
                <Sparkles size={16} />
                Generate Specification
              </button>
            </>
          )}
        </div>

        {/* Agent Stream Panel for app generation (Claude Code mode) */}
        {agentAppPanelOpen && generationMode === 'claude_code' && (
          <div className="responsive-panel" style={{
            width: 420,
            flexShrink: 0,
            borderLeft: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideInRight 250ms ease-out',
          }}>
            <AgentStreamPanel
              messages={appAgent.messages}
              isStreaming={appAgent.isStreaming}
              wsStatus={appAgent.wsStatus}
              error={appAgent.error}
              agentProgress={appAgent.agentProgress}
              onCancel={() => {
                appAgent.cancelTask()
                setGeneratingApp(false)
              }}
              onClear={appAgent.clearMessages}
              onClose={() => {
                setAgentAppPanelOpen(false)
                setGeneratingApp(false)
              }}
              onPermissionResponse={appAgent.respondToPermission}
              title="Generating Application"
            />
          </div>
        )}
      </div>
    )
  }

  const themeColor = appData.application.theme_color || '#3F51B5'
  const isFolder = selectedNode?.obj_type === '-'
  const noun = tab?.noun || 'screen'
  const nouns = tab?.nouns || 'screens'
  const selStatus = selectedNode ? statusOf(selectedNode) : null
  const selJourney = selectedNode ? journeyObject(selectedNode) : null
  const selShown = selectedNode ? screenStatus(selStatus, selJourney?.last_test) : null
  const SelIcon = (selectedNode && TYPE_ICON[selectedNode.obj_type]) || FileText
  const designerRoute = selectedNode ? (vocabulary.designerRoutes as Record<string, string>)[selectedNode.obj_type] : undefined
  const designerName = selectedNode ? (vocabulary.designerNames as Record<string, string>)[selectedNode.obj_type] : undefined
  const designerHint = selectedNode ? (vocabulary.designerHints as Record<string, string>)[selectedNode.obj_type] : undefined
  const codeType = !!selectedNode && !isFolder && !['X', 'V', 'D', 'S'].includes(selectedNode.obj_type)
  const previewView = selectedNode ? previewTabFor(selectedNode, modelExists) : 'details'
  const hasPreview = previewView !== 'details'
  const isPreviewView = hasPreview && activeTab === previewView
  // ⋯ menu → a view. null = the object's Preview (and stop pinning a view).
  // Edit details and the designer (with its Code view) open the way the user
  // last picked — in the page, a dialog or full screen (objectViewStore). In
  // the page, the view stays pinned while you click through the tree; a
  // dialog / full screen covers the tree, so Close returns to the preview.
  const pinFor = (view: 'layout' | 'details' | 'spec' | null, modes = viewModes) =>
    view === 'spec' || (view === 'layout' && modes.designer === 'tab') || (view === 'details' && modes.details === 'tab')
      ? view : null
  const chooseView = (view: 'layout' | 'details' | 'spec' | null) => {
    pinnedViewRef.current = pinFor(view)
    const target = view ?? previewView
    if (target !== activeTab) guardEditorNav(() => setActiveTab(target))
  }
  // Make changes: a transaction changes right in its preview (the Make
  // changes mode); anything else opens in its designer.
  const makeInPlace = !!selectedNode && selectedNode.obj_type === 'T' && modelExists && canEditObjects
  // A website — and each page of it — changes in place in the site's own
  // preview (docs/website_make_changes.md). Its header / footer pages and
  // collection templates are not pages a visitor opens: they keep the designer.
  const siteOfSelected = (() => {
    if (!selectedNode || !appData) return null
    if (selectedNode.obj_type === 'X') return selectedNode
    if (selectedNode.obj_type !== 'S' || !selectedNode.parent_id) return null
    const parent = appData.navigation.find((n) => n.id === selectedNode.parent_id)
    if (!parent || parent.obj_type !== 'X') return null
    const { main } = arrangeSiteChildren(parent, getChildren(appData.navigation, parent.id))
    return main.some((n) => n.id === selectedNode.id) ? parent : null
  })()
  const siteInPlace = !!siteOfSelected && canEditObjects
  const canMakeChanges = !!selectedNode && !isFolder
    && (makeInPlace || siteInPlace || (!!designerRoute && NODE_HAS_DESIGNER.includes(selectedNode.obj_type)))
  const makeChanges = () => {
    if (siteInPlace) {
      pinnedViewRef.current = null
      const request = Date.now()
      if (activeTab === previewView) setSiteEditRequest(request)
      else guardEditorNav(() => { setActiveTab(previewView); setSiteEditRequest(request) })
      return
    }
    if (!makeInPlace) { chooseView('layout'); return }
    pinnedViewRef.current = null
    const request = Date.now()
    if (activeTab === 'livepreview') setMakeChangesRequest(request)
    else guardEditorNav(() => { setActiveTab('livepreview'); setMakeChangesRequest(request) })
  }
  const changeViewMode = (kind: ObjectViewKind, mode: ObjectViewMode) => {
    setViewMode(kind, mode)
    const view = kind === 'designer' ? 'layout' : 'details'
    if (pinnedViewRef.current === view || pinnedViewRef.current === null) {
      pinnedViewRef.current = pinFor(view, { ...viewModes, [kind]: mode })
    }
  }
  const closeObjectView = () => {
    setCodeTabOpen(false)
    setObjectCodeOpen(null)
    chooseView(null)
  }
  const objectTitle = selectedNode ? (selectedNode.title || selectedNode.obj_name) : ''
  // A website's preview address (its slug, else its object name).
  const siteUrlFor = (site: FlatNavItem) => {
    let siteSlug = site.obj_name
    try {
      const spec = JSON.parse(site.functional_specs || '{}')
      if (spec.slug) siteSlug = spec.slug
    } catch { /* use obj_name */ }
    return `/site/${encodeURIComponent(user?.user_id || '')}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}`
  }
  // Make changes added a page or changed the website's settings on the server:
  // re-read the tree — unless it holds unsaved moves, which a reload would drop.
  const reloadAfterSiteEdit = () => {
    if (navDirty) {
      toast('Save the tree to see the website’s new pages in it.')
      return
    }
    const keep = selectedNode?.id
    getJsonRaw(project)
      .then((raw) => {
        const data = normalizeAppData(raw)
        setAppData(data)
        const node = keep ? data.navigation.find((n: FlatNavItem) => n.id === keep) : undefined
        if (node) setSelectedNode(node)
      })
      .catch((err) => {
        console.error('[site-edit] the tree could not be re-read', err)
        toast.error('The website changed, but the tree could not be refreshed. Reload the page to see it.')
      })
  }

  return (
    <div
      className="split-layout swb"
      style={{ display: 'flex', flex: 1, minHeight: 0, gap: 16, animation: 'fadeIn 0.25s ease-out' }}
    >
      {/* Mobile nav backdrop */}
      {mobileNavOpen && <div className="split-nav-backdrop" onClick={() => setMobileNavOpen(false)} />}

      {/* ─── Left Panel: the app's menu tree (shared AppTree) ─── */}
      <div
        className={`split-nav ${mobileNavOpen ? '' : 'split-nav-hidden'}`}
        style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <AppTree
          navigation={appData.navigation}
          selectedId={selectedNode?.id || null}
          onSelect={(item) => {
            if (item.id === selectedNode?.id) { setMobileNavOpen(false); return }
            guardEditorNav(() => {
              wantPreviewRef.current = !pinnedViewRef.current
              setSelectedNode(item)
              setMobileNavOpen(false)
            })
          }}
          onDeselect={() => guardEditorNav(() => setSelectedNode(null))}
          expandedIds={expandedIds}
          onToggle={toggleExpand}
          onExpand={handleAutoExpand}
          onMove={handleMove}
          statusOf={statusOf}
          types={tab?.types ?? null}
          keepIds={addedHere}
          heading="Your app"
          nouns={nouns}
          addLabel={`Add ${noun}`}
          onAdd={handleAddRootNode}
          onSave={handleSaveAll}
          saving={saving}
          dirty={navDirty}
          scrollRef={treeScrollRef}
        />
      </div>

      {/* ─── Right Panel: the selected object ─── */}
      <div
        ref={centerScrollRef}
        className="split-content swb-card"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '18px 20px', minWidth: 0 }}
      >
        {/* Mobile floating nav toggle */}
        <button className="split-nav-toggle" onClick={() => setMobileNavOpen(true)}>
          <span className="material-icons" style={{ fontSize: 17 }}>account_tree</span>
          Your app
        </button>

        {!selectedNode ? (
          <CheckScreensPanel
            project={project}
            noun={noun}
            nouns={nouns}
            types={tab?.types ?? null}
            onAdd={handleAddRootNode}
            onOpen={(o) => {
              const node = appData.navigation.find((n) => n.id === o.nav_id)
                || appData.navigation.find((n) => n.obj_type === o.obj_type && n.obj_name === o.obj_name)
              if (!node) { toast('It is planned but not on the menu yet'); return }
              guardEditorNav(() => {
                wantPreviewRef.current = !pinnedViewRef.current
                setSelectedNode(node)
              })
            }}
          />
        ) : (
          /* ── Selected Node Content ── */
          <div style={{ animation: 'fadeIn 0.2s ease-out', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid var(--color-border)' }}>
              <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--color-accent-weak)', color: 'var(--color-accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <SelIcon size={20} />
              </span>
              <div style={{ flex: '1 1 160px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedNode.title || selectedNode.obj_name}
                </h2>
                <span style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 8px', alignItems: 'center', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  <span className="swb-chip swb-chip-neutral" style={{ color: 'var(--color-text)' }}>{typeName(selectedNode.obj_type)}</span>
                  {selShown && <span className={`swb-chip swb-chip-${selShown.cls}`}>{selShown.label}</span>}
                </span>
              </div>
              {!isFolder && (
                <ScreenTestLink
                  result={selJourney?.last_test}
                  built={selJourney?.built}
                  running={!!journey?.tests_running}
                  onOpen={() => setRecordOpen(true)}
                  onTest={() => void runAction(`test:${selectedNode.obj_type}:${selectedNode.obj_name}`)}
                />
              )}
              {!isFolder && canMakeChanges && (
                <button type="button" className="swb-btn swb-btn-secondary" onClick={makeChanges}
                  title={selectedNode.obj_type === 'T'
                    ? 'Change this screen right here: fields, buttons, rules'
                    : siteInPlace
                    ? `Change this ${selectedNode.obj_type === 'X' ? 'website' : 'page'} right here: its look, sections, words, menu and pages`
                    : `Change it in the ${designerName || 'designer'}`}
                  data-tw-action="make-changes">
                  <PencilRuler size={15} /> Make changes
                </button>
              )}

              {/* ⋯ menu — business words first; the developer actions grouped below. */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  type="button"
                  className="swb-btn swb-btn-secondary"
                  aria-label={`More actions for ${selectedNode.title || selectedNode.obj_name}`}
                  aria-haspopup="menu"
                  aria-expanded={nodeMenuOpen}
                  onClick={(e) => { e.stopPropagation(); setNodeMenuOpen((o) => !o) }}
                  style={{ width: 40, padding: 0 }}
                >
                  <MoreHorizontal size={18} />
                </button>
                {nodeMenuOpen && (
                  <div role="menu" className="swb-menu" onClick={(e) => e.stopPropagation()}>
                    {!isFolder && hasPreview && (
                      <button type="button" role="menuitem" className="swb-menu-item"
                        aria-current={isPreviewView ? 'true' : undefined}
                        onClick={() => { setNodeMenuOpen(false); chooseView(null) }}>
                        <Eye size={17} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 2 }} />
                        <span style={{ flex: 1 }}><strong>Preview</strong><span className="swb-menu-line">See it as people will use it</span></span>
                        {isPreviewView && <Check size={15} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 3 }} />}
                      </button>
                    )}
                    {!isFolder && (
                      <button type="button" role="menuitem" className="swb-menu-item"
                        aria-current={activeTab === 'spec' ? 'true' : undefined}
                        onClick={() => { setNodeMenuOpen(false); chooseView('spec') }}>
                        <FileText size={17} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 2 }} />
                        <span style={{ flex: 1 }}><strong>View specification</strong><span className="swb-menu-line">What it does, its rules and what happens next</span></span>
                        {activeTab === 'spec' && <Check size={15} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 3 }} />}
                      </button>
                    )}
                    {designerRoute && NODE_HAS_DESIGNER.includes(selectedNode.obj_type) && (
                      <button type="button" role="menuitem" className="swb-menu-item"
                        aria-current={activeTab === 'layout' ? 'true' : undefined}
                        onClick={() => { setNodeMenuOpen(false); chooseView('layout') }}>
                        <Edit3 size={17} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 2 }} />
                        <span style={{ flex: 1 }}><strong>Open in {designerName || 'designer'}</strong><span className="swb-menu-line">For detailed changes{designerHint ? `: ${designerHint}` : ''}</span></span>
                        {activeTab === 'layout' && <Check size={15} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 3 }} />}
                      </button>
                    )}
                    <button type="button" role="menuitem" className="swb-menu-item"
                      aria-current={activeTab === 'details' && !isFolder ? 'true' : undefined}
                      onClick={() => { setNodeMenuOpen(false); chooseView('details') }}>
                      <Pencil size={17} style={{ color: 'var(--color-text-secondary)', flex: 'none', marginTop: 2 }} />
                      <span style={{ flex: 1 }}><strong>Edit details</strong><span className="swb-menu-line">Title, icon, description and what it should do</span></span>
                      {activeTab === 'details' && !isFolder && <Check size={15} style={{ color: 'var(--color-accent)', flex: 'none', marginTop: 3 }} />}
                    </button>
                    {!isFolder && selectedNode.obj_type !== 'X' && (
                      <button type="button" role="menuitem" className="swb-menu-item" disabled={!selJourney?.built}
                        onClick={() => { setNodeMenuOpen(false); window.open(runUrl(user?.tenant_id, project, selectedNode.obj_name), '_blank', 'noopener') }}>
                        <Play size={17} style={{ color: 'var(--color-text-secondary)', flex: 'none', marginTop: 2 }} />
                        <span><strong>Try it full screen</strong><span className="swb-menu-line">Opens it in the running app</span></span>
                      </button>
                    )}
                    {!isFolder && (
                      <button type="button" role="menuitem" className="swb-menu-item"
                        onClick={() => { setNodeMenuOpen(false); setHistoryOpen(true) }}>
                        <History size={17} style={{ color: 'var(--color-text-secondary)', flex: 'none', marginTop: 2 }} />
                        <span><strong>See change history</strong><span className="swb-menu-line">Saved versions that touched it</span></span>
                      </button>
                    )}
                    {!isFolder && selectedNode.obj_type !== 'X' && (modelExists || selectedNode.obj_type === 'T') && (
                      <>
                        <div className="swb-menu-sep" />
                        <div className="swb-menu-group">Developer</div>
                        {/* Generate Code (transaction-class types with a model) */}
                        {modelExists && codeType && (
                          <NodeMenuItem
                            icon={generatingDb ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Database size={14} />}
                            label={generatingDb ? 'Generating…' : 'Generate Code'}
                            disabled={generatingDb}
                            onClick={() => { setNodeMenuOpen(false); handleGenerateDatabase() }}
                          />
                        )}
                        {/* Design Workflow (transactions only) */}
                        {selectedNode.obj_type === 'T' && (
                          <NodeMenuItem
                            icon={<GitBranch size={14} />}
                            label="Design Workflow"
                            onClick={() => { setNodeMenuOpen(false); handleDesignWorkflow() }}
                          />
                        )}
                        {modelExists && (
                          <NodeMenuItem
                            icon={<Download size={14} />}
                            label="Download Model"
                            onClick={() => { setNodeMenuOpen(false); handleDownloadModel() }}
                          />
                        )}
                        {modelExists && codeType && (
                          <NodeMenuItem
                            icon={deletingDb ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={14} />}
                            label={deletingDb ? 'Deleting…' : 'Delete Codes'}
                            danger
                            disabled={deletingDb}
                            onClick={() => { setNodeMenuOpen(false); handleDeleteDatabase() }}
                          />
                        )}
                      </>
                    )}
                    <div className="swb-menu-sep" />
                    <button type="button" role="menuitem" className="swb-menu-item swb-menu-danger"
                      onClick={() => { setNodeMenuOpen(false); handleDeleteNode() }}>
                      <Trash2 size={17} style={{ flex: 'none', marginTop: 2 }} />
                      <span><strong>Remove from the app</strong><span className="swb-menu-line" style={{ color: 'inherit', opacity: 0.85 }}>Takes it out of the menu{isFolder ? ', with everything inside it' : ''}</span></span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Top action row — only folders (add inside) and External
                Websites (Add Page / Preview Site) keep a button row here. */}
            {(isFolder || selectedNode.obj_type === 'X') && (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                {isFolder && (
                  <button className="swb-btn swb-btn-secondary swb-btn-sm" onClick={handleAddChildNode}>
                    <Plus size={14} />
                    Add {noun} inside this folder
                  </button>
                )}
                {/* External Website (X) is folder-like — let users add
                    child Smart Pages (which become marketing pages). */}
                {selectedNode.obj_type === 'X' && (
                  <>
                    <button className="swb-btn swb-btn-secondary swb-btn-sm" onClick={handleAddChildNode}>
                      <Plus size={14} />
                      Add Page
                    </button>
                    {user && (() => {
                      let siteSlug = selectedNode.obj_name
                      try {
                        const spec = JSON.parse(selectedNode.functional_specs || '{}')
                        if (spec.slug) siteSlug = spec.slug
                      } catch { /* use obj_name */ }
                      const url = `/site/${encodeURIComponent(user.user_id)}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}`
                      return (
                        <button className="swb-btn swb-btn-primary swb-btn-sm" onClick={() => window.open(url, '_blank')}>
                          <Eye size={14} />
                          Preview Site
                        </button>
                      )
                    })()}
                  </>
                )}
              </div>
            )}

            {/* Tab strip — only for non-folder nodes. Folders have nothing
                to preview, so they keep the existing single-pane layout.
                Right side of the strip hosts a portal slot that the
                active preview pane fills with its own toolbar (device
                toggle, form/grid, etc.). */}
            {!isFolder && (
              <>
                {/* Responsive rules for whatever the active pane portals
                    into the slot. Hide controls progressively as the
                    panel narrows; tabs stay accessible at every width. */}
                <style>{`
                  .preview-toolbar { gap: 6px !important; }
                  @media (max-width: 720px) { .preview-toolbar .tb-device { display: none !important; } }
                  @media (max-width: 560px) { .preview-toolbar .tb-forms { display: none !important; } }
                  @media (max-width: 460px) { .preview-toolbar .tb-reload { display: none !important; } }
                  @media (max-width: 380px) { .preview-toolbar .tb-mode { display: none !important; } }
                `}</style>
                {/* No view tab strip: the ⋯ menu switches between Preview,
                    Specification, the designer and Details. Details and the
                    designer open in the page, a dialog or full screen — the
                    user's remembered choice (switcher on the view's bar). */}
              </>
            )}

            {/* Design — the full designer for this object type, shown in the
                page, a dialog or full screen (ObjectView). The designer's own
                header carries Save / undo / redo; its own full-screen toggle
                is hidden because the view's switcher does that. Mounted
                lazily so the heavy editor only loads when opened. */}
            {!isFolder && activeTab === 'layout' && NODE_HAS_DESIGNER.includes(selectedNode.obj_type) && (
              <ObjectView
                mode={viewModes.designer}
                onModeChange={(m) => changeViewMode('designer', m)}
                icon={<Edit3 size={13} />}
                label={designerName || 'Designer'}
                title={objectTitle}
                onClose={closeObjectView}
              >
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  {(() => {
                    const common = {
                      embedded: true as const,
                      hostFullscreen: true,
                      embedProject: project,
                      embedObjName: selectedNode.obj_name,
                      onDirtyChange: handleEditorDirtyChange,
                      registerSave: handleRegisterEditorSave,
                      onSaved: handleEditorSaved,
                    }
                    switch (selectedNode.obj_type) {
                      case 'S': return (
                        <PageDesignerPage
                          {...common}
                          // Before-load access event "Open in code editor" →
                          // reveal the object's own closeable Code tab with the
                          // resolved BL file preopened (same as transactions).
                          onOpenCodeFile={(relPath) => {
                            setObjectCodeOpen(relPath ? { file: relPath, nonce: Date.now() } : null)
                            setCodeTabOpen(true)
                            setActiveTab('objectcode')
                          }}
                        />
                      )
                      case 'V': return <VisualDesignerPage {...common} />
                      case 'D': return <DashboardDesignerPage {...common} />
                      case 'M': return <SchemaDesignerPage {...common} />
                      case 'G': return <GenProcessDesignerPage {...common} />
                      case 'R': return <ReportDesignerPage {...common} />
                      case 'K': return <DocumentDesignerPage {...common} />
                      case 'E': return <DocumentFormDesignerPage {...common} />
                      case 'U': return <WizardDesignerPage {...common} />
                      default: return (
                        <TransactionDesignerPage
                          {...common}
                          // "Open in code editor" reveals the object's own
                          // Code tab (hidden until now) with the resolved file
                          // preopened, and switches to it.
                          onOpenCodeFile={(relPath) => {
                            setObjectCodeOpen(relPath ? { file: relPath, nonce: Date.now() } : null)
                            setCodeTabOpen(true)
                            setActiveTab('objectcode')
                          }}
                        />
                      )
                    }
                  })()}
                </div>
              </ObjectView>
            )}

            {/* Live Preview tab — production-grade runtime for transactions,
                live chart preview for visuals/dashboards. Only rendered when
                the tab is active so the data fetch / schema deploy don't happen
                until the user opens it. Sized like the Design tab. */}
            {!isFolder && activeTab === 'livepreview' && hasLivePreview(selectedNode.obj_type, modelExists) && (
              selectedNode.obj_type === 'T' ? (
                <div style={{ flex: 1, minHeight: 460, display: 'flex', flexDirection: 'column' }}>
                  <LivePreviewLazyPane project={project} objName={selectedNode.obj_name}
                    makeChangesRequest={makeChangesRequest} onMakeChangesOpened={() => setMakeChangesRequest(0)} />
                </div>
              ) : (
                <div style={{
                  flex: 1, minHeight: 460,
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                  overflow: 'hidden', display: 'flex', flexDirection: 'column',
                }}>
                  {selectedNode.obj_type === 'J'
                    ? <div style={{ flex: 1, minHeight: 0, padding: 12, display: 'flex', flexDirection: 'column' }}>
                        <FlowRunPane project={project} objName={selectedNode.obj_name} />
                      </div>
                    : selectedNode.obj_type === 'V'
                    ? <VisualLivePreviewPane project={project} objName={selectedNode.obj_name} />
                    : selectedNode.obj_type === 'G'
                      ? <GenProcessLivePreviewPane project={project} objName={selectedNode.obj_name} />
                      : selectedNode.obj_type === 'R'
                        ? <ReportLivePreviewPane project={project} objName={selectedNode.obj_name} />
                        : selectedNode.obj_type === 'K'
                          ? <DocumentRepositoryPane project={project} objName={selectedNode.obj_name} />
                          : selectedNode.obj_type === 'U'
                            ? <WizardLivePreviewPane project={project} objName={selectedNode.obj_name} />
                          : <DashboardLivePreviewPane project={project} objName={selectedNode.obj_name} />}
                </div>
              )
            )}

            {/* Smart Page Live Preview — native React runtime that
                replaces the Angular iframe for in-app preview.
                Document-mode pages get a docs list + inline editor;
                internal/external pages render a stateless preview. */}
            {!isFolder && activeTab === 'smartpage-livepreview' && selectedNode.obj_type === 'S' && (
              siteEditRequest && siteOfSelected && user ? (
                // A website page's Make changes: the website's own preview,
                // opened on this page; Done comes back to the page preview.
                <SiteEditFrame siteUrl={siteUrlFor(siteOfSelected)} title={siteOfSelected.title || siteOfSelected.obj_name}
                  editRequest={siteEditRequest} editPage={selectedNode.obj_name}
                  onModeEnd={() => setSiteEditRequest(0)} onSiteChanged={reloadAfterSiteEdit} />
              ) : (
                <SmartPageLivePreviewPane project={project} objName={selectedNode.obj_name} />
              )
            )}

            {/* External Website Live Preview — renders the public site inline
                in an iframe (same viewer the published site uses). Opening it in
                a new tab is the Preview Site button above. */}
            {!isFolder && activeTab === 'site-livepreview' && selectedNode.obj_type === 'X' && user && (
              <SiteEditFrame siteUrl={siteUrlFor(selectedNode)} title={selectedNode.title || selectedNode.obj_name}
                editRequest={siteEditRequest} onSiteChanged={reloadAfterSiteEdit} />
            )}

            {/* Object Code — the SAME source editor as the project-wide
                Code page (file tree + CodeMirror + AI assistant), scoped to
                this transaction's src folder so it lists only this object's
                business-logic files. Opened from the designer and shown the
                same way; "Back to designer" returns there. */}
            {!isFolder && activeTab === 'objectcode'
              && (selectedNode.obj_type === 'T' || selectedNode.obj_type === 'S') && modelExists && (
              <ObjectView
                mode={viewModes.designer}
                onModeChange={(m) => changeViewMode('designer', m)}
                icon={<FileCode size={13} />}
                label="Code"
                title={objectTitle}
                onClose={closeObjectView}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', flexShrink: 0, borderBottom: '1px solid var(--color-border)' }}>
                  <button
                    type="button"
                    className="swb-btn swb-btn-secondary swb-btn-sm"
                    onClick={() => {
                      setCodeTabOpen(false)
                      setObjectCodeOpen(null)
                      setActiveTab('layout')
                    }}
                  >
                    <Edit3 size={13} /> Back to designer
                  </button>
                </div>
                <SourceEditor
                  project={project}
                  embedded
                  scopeDir={`${selectedNode.obj_type === 'S' ? 'page_models' : 'transaction_models'}/${selectedNode.obj_name.replace(/ /g, '_')}`}
                  initialFile={objectCodeOpen?.file}
                  openNonce={objectCodeOpen?.nonce}
                />
              </ObjectView>
            )}

            {/* Specification — this object's part of the plan, read-only. */}
            {!isFolder && activeTab === 'spec' && (
              <SpecView
                node={selectedNode}
                onBack={() => chooseView(null)}
                onEdit={() => chooseView('details')}
                onPlan={() => void runAction('tab:plan')}
              />
            )}

            {/* Details tab — folders always land here too. The spec is now
                edited inline (the old "Edit Specification" modal merged in);
                Save persists immediately and highlights when dirty. For an
                object with a preview of its own, Edit details opens the way
                the user picked (page / dialog / full screen); a folder, or an
                object whose only view IS Details, keeps it in the page. */}
            {(isFolder || activeTab === 'details') && (() => {
              const detailsBody = (
              <>
                {/* Generate / Re-Generate Model lives at the top of Details so
                    it's easy to reach. Primary until a model exists, then a
                    quieter secondary "Re-Generate". */}
                {/* A wizard (U) has no generation pipeline here: it is
                    designed on its Design tab or by the Ask-AI agent, and the
                    transaction-class generator would write the wrong shape.
                    Nor does an Integration Flow (J): its input form is made
                    from the flow's inputs when the item is saved. */}
                {!isFolder && selectedNode.obj_type !== 'X' && selectedNode.obj_type !== 'J' && modelCheckedFor === selectedNode.obj_name && !modelExists && (
                  <div className="swb-notice swb-notice-info" style={{ marginBottom: '1rem' }}>
                    <span style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column' }}>
                      <strong>Planned — not built yet</strong>
                      <span className="swb-muted">
                        {selectedNode.description || 'Describe what it should do below, then build it — or ask the AI.'}
                      </span>
                    </span>
                    <button type="button" className="swb-btn swb-btn-secondary" onClick={() => {
                      prefillDock(`Build the ${typeName(selectedNode.obj_type).toLowerCase()} "${selectedNode.title || selectedNode.obj_name}" from its plan.`)
                      setDockOpen(true)
                    }}>
                      Ask the AI to build it
                    </button>
                  </div>
                )}
                {!isFolder && selectedNode.obj_type !== 'X' && selectedNode.obj_type !== 'U' && selectedNode.obj_type !== 'J' && (
                  <div style={{ display: 'flex', marginBottom: '1.25rem' }}>
                    <button
                      className={`btn btn-sm ${modelExists ? 'btn-secondary' : 'btn-primary'}`}
                      onClick={handleGenerateModel}
                      disabled={generatingModel}
                    >
                      {generatingModel ? (
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <Play size={14} />
                      )}
                      {generatingModel ? 'Generating…' : modelExists ? 'Re-Generate Model' : 'Generate Model'}
                    </button>
                    {/* A report that already exists as a Jasper design is
                        imported, not generated. */}
                    {selectedNode.obj_type === 'R' && (
                      <>
                        <input
                          ref={reportImportRef}
                          type="file"
                          accept=".zip,.jrxml,.xml"
                          multiple
                          style={{ display: 'none' }}
                          onChange={handleImportReport}
                        />
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ marginLeft: 8 }}
                          onClick={() => reportImportRef.current?.click()}
                          disabled={importingReport || generatingModel}
                          title="Import an existing Jasper report — a .zip bundle (main report + subreports) or .jrxml files"
                        >
                          {importingReport ? (
                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Upload size={14} />
                          )}
                          {importingReport ? 'Importing…' : 'Import JRXML'}
                        </button>
                      </>
                    )}
                  </div>
                )}
                <div className="card" style={{ marginBottom: '1.25rem' }}>
                  <EditNodeModal
                    key={`${selectedNode.id}:${formEpoch}`}
                    inline
                    node={selectedNode}
                    isNew={false}
                    project={project}
                    generationMode={generationMode}
                    effortConfig={effortConfig}
                    navItems={appData.navigation}
                    onConvertMenus={handleConvertMenus}
                    onSave={handleSaveNodeInline}
                    onClose={() => { /* inline editor — nothing to close */ }}
                  />
                </div>
              </>
              )
              if (isFolder || !hasPreview) return detailsBody
              return (
                <ObjectView
                  mode={viewModes.details}
                  onModeChange={(m) => changeViewMode('details', m)}
                  icon={<Pencil size={13} />}
                  label="Edit details"
                  title={objectTitle}
                  onClose={closeObjectView}
                  scroll
                >
                  {detailsBody}
                </ObjectView>
              )
            })()}
          </div>
        )}
      </div>

      {/* Agent Stream Panel for model generation (Claude Code mode) */}
      {agentModelPanelOpen && generationMode === 'claude_code' && (
        <div className="responsive-panel" style={{
          width: 420,
          flexShrink: 0,
          borderLeft: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInRight 250ms ease-out',
        }}>
          <AgentStreamPanel
            messages={modelAgent.messages}
            isStreaming={modelAgent.isStreaming}
            wsStatus={modelAgent.wsStatus}
            error={modelAgent.error}
            agentProgress={modelAgent.agentProgress}
            onCancel={() => {
              modelAgent.cancelTask()
              setGeneratingModel(false)
            }}
            onClear={modelAgent.clearMessages}
            onClose={() => {
              setAgentModelPanelOpen(false)
              setGeneratingModel(false)
            }}
            onPermissionResponse={modelAgent.respondToPermission}
            title="Generating Model"
          />
        </div>
      )}

      {/* ─── Edit Modal (Backdrop) ─── */}
      {editModalOpen && (
        <>
          <div
            onClick={() => setEditModalOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 99,
            }}
          />
          <EditNodeModal
            node={editingNode}
            isNew={isNewNode}
            project={project}
            generationMode={generationMode}
            effortConfig={effortConfig}
            navItems={appData.navigation}
            onSave={handleSaveNode}
            onClose={() => setEditModalOpen(false)}
          />
        </>
      )}

      {recordOpen && selectedNode && (
        <ScreenTestModal
          project={project}
          obj={{ obj_type: selectedNode.obj_type, obj_name: selectedNode.obj_name, title: selectedNode.title || selectedNode.obj_name }}
          onClose={() => setRecordOpen(false)}
        />
      )}
      {historyOpen && selectedNode && (
        <ChangeHistoryModal project={project} node={selectedNode} onClose={() => setHistoryOpen(false)} />
      )}

      {/* Unsaved-changes guard for the embedded designer (Design tab). */}
      <DiscardChangesModal
        open={guardOpen}
        saving={editorSaving}
        message={navDirty && !editorDirty
          ? 'The menu has changes that are not saved yet (added, moved or removed items). Save them before leaving, or discard them and continue?'
          : 'The designer has unsaved changes. Save them before leaving, or discard them and continue?'}
        onCancel={handleGuardStay}
        onDiscard={handleGuardDiscard}
        onSave={handleGuardSaveContinue}
      />
    </div>
  )
}
